<template>
	<div class="site-plan-container w-full h-full" :class="{ 'is-admin': isAdmin }" :style="{ touchAction: isEmbedded && isMobile ? 'auto' : 'none' }">
		<div
			class="map-wrapper relative"
			ref="mapWrapper"
			:style="{ touchAction: isEmbedded && isMobile ? 'auto' : 'none' }"
		>
			<!-- Admin-only Bulk button -->
			<div v-if="isAdmin && !isLotFocusedMode && !isScreenshotMode" class="absolute top-32 right-4 z-50 flex items-center gap-2">
				<MapSwitcher
					ref="mapSwitcherRef"
					:maps="allMaps"
					:active-id="activeMapId"
					:loading="isLoadingMaps"
					@preview="handleMapPreview"
					@set-active="handleSetActive"
					@cancel-preview="handleCancelPreview"
				/>
				<button
					@click.stop="openBulkPopup"
					class="rounded-full bg-white/90 border border-black/10 px-4 py-1.5 text-xs font-semibold tracking-wide uppercase shadow-sm hover:bg-black hover:text-white transition-colors"
				>
					Bulk
				</button>
				<button
					@click.stop="openScreenshotModal"
					class="rounded-full bg-white/90 border border-black/10 px-4 py-1.5 text-xs font-semibold tracking-wide uppercase shadow-sm hover:bg-black hover:text-white transition-colors"
				>
					Screenshots
				</button>
			</div>
			
			<div 
				class="map-content" 
				ref="mapContent"
			>
				<!-- SVG will be created by SVG.js -->
			</div>

			<!-- Screenshot overlay - outside map-content -->
			<div 
				v-if="isScreenshotMode" 
				class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 pointer-events-auto"
				style="backdrop-filter: blur(2px);"
			>
				<div class="bg-white rounded-lg px-8 py-6 shadow-xl text-center max-w-md">
					<div v-if="screenshotStatus === 'success'" class="text-sm font-semibold text-green-600 mb-2">Complete</div>
					<div v-else-if="screenshotStatus === 'error'" class="text-sm font-semibold text-red-600 mb-2">Error</div>
					<div v-else class="mb-3">
						<div class="w-8 h-8 border-4 border-black/20 border-t-black rounded-full animate-spin mx-auto"></div>
					</div>
					<div class="text-sm text-black">{{ screenshotOverlayMessage }}</div>
				</div>
			</div>

			<!-- Download-from-URL overlay -->
			<div
				v-if="showDownloadOverlay && !isScreenshotMode"
				class="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 pointer-events-auto"
				style="backdrop-filter: blur(2px);"
			>
				<div class="bg-white rounded-lg px-8 py-6 shadow-xl text-center max-w-md flex flex-col gap-4">
					<p class="text-sm text-black">Download will start shortly.</p>
					<p class="text-xs text-[#767676]">If it didn’t start, use the button below.</p>
					<div class="flex flex-col gap-2">
						<button
							v-if="downloadStageFromUrl && downloadTypeFromUrl"
							type="button"
							class="w-full rounded-full bg-[#0C0C0C] py-3 px-4 text-xs font-semibold text-white uppercase tracking-wider disabled:opacity-60"
							:disabled="(downloadTypeFromUrl === 'map' && downloadingStage === downloadStageFromUrl) || (downloadTypeFromUrl === 'pricelist' && downloadingPriceList === downloadStageFromUrl)"
							@click="downloadTypeFromUrl === 'map' ? downloadMap(downloadStageFromUrl) : beginPriceListDownload(downloadStageFromUrl)"
						>
							{{ downloadTypeFromUrl === 'map' ? `Download Stage ${downloadStageFromUrl} Map` : `Download Stage ${downloadStageFromUrl} Price List` }}
						</button>
						<button
							type="button"
							class="w-full rounded-full bg-[#E6E6E6] py-3 px-4 text-xs font-semibold text-black uppercase tracking-wider"
							@click="dismissDownloadOverlay"
						>
							See map
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Lot Popup -->
		 <div 
			v-if="showPopup && !isLotFocusedMode && !isScreenshotMode" 
			data-lot-popup 
			class="fixed bg-white shadow-lg px-4 py-6 w-full flex flex-col justify-between z-50 transition-all"
			:class="{
				'top-[20vh] right-[10vw] max-w-md': !isMobile,
				'bottom-0 left-0 right-0 max-w-none max-h-[80vh] overflow-y-auto': isMobile
			}">
			<div 
				@touchend.prevent.stop="closePopup"
				@click.stop="closePopup" 
				class="absolute top-1.5 right-1.5 w-5 h-5 rounded-sm bg-[#E6E6E6] flex items-center justify-center cursor-pointer"
				>
				<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="9.678" height="9.678" viewBox="0 0 9.678 9.678">
				<defs>
				<clipPath id="clip-path">
				<path id="Path_30589" data-name="Path 30589" d="M158.678-1176.025,157.7-1177l-3.864,3.865L149.975-1177l-.975.975,3.864,3.864L149-1168.3l.975.975,3.864-3.864,3.864,3.864.975-.975-3.864-3.865Z" transform="translate(-149 1177)" fill="none" clip-rule="evenodd"/>
				</clipPath>
				<clipPath id="clip-path-2">
				<path id="Path_30588" data-name="Path 30588" d="M-48-1128.679H300.416V-1278H-48Z" transform="translate(48 1278)" fill="none"/>
				</clipPath>
				</defs>
				<g id="Group_21229" data-name="Group 21229" transform="translate(0 0)" clip-path="url(#clip-path)">
				<g id="Group_21228" data-name="Group 21228" transform="translate(-136.186 -69.821)" clip-path="url(#clip-path-2)">
				<path id="Path_30587" data-name="Path 30587" d="M144-1165.409h16.591V-1182H144Z" transform="translate(-11.27 1248.365)"/>
				</g>
				</g>
				</svg>
			</div>
			<div v-if="selectedLot?.imagesSection?.length"
				class="image-container rounded-none mb-3 mt-2 aspect-[364/242.97]"
				:class="{ 'lot-popup-image--bleed': isMobile }">
			<img
				class="w-full h-full object-cover"
				:src="selectedLot.imagesSection[0].url"
				:alt="selectedLot.imagesSection[0].alt || selectedLot.imagesSection[0].title || ''">
			</div>
			<div class="space-y-2">
				<div v-if="selectedLot?.sectionType && !isEditMode" class="text-sm text-[#767676] font-semibold">
					{{ selectedLot.__typename === 'townhouse_Entry'
						? (selectedLot.superlotType ? 'Townhouse Superlot' : 'Townhouse')
						: (selectedLot.sectionTypeLabel || selectedLot.sectionType)
					}}
				</div>
				<div class="flex items-center gap-4">
					<div class="flex items-center w-full justify-between">
						<div class="flex items-center gap-3">
							<div class="text-2xl text-black leading-none">Lot {{ selectedLot?.title }}</div>
							<div class="text-2xl text-[#DB886E] leading-none" v-if="selectedLot?.status === 'available'">
								<template v-if="selectedLot?.price">
									${{ selectedLot.price.toLocaleString() }}
									<span class="text-xs">inc. GST</span>
								</template>
								<template v-else>
									POA
								</template>
							</div>
						</div>
						<div 
							class="uppercase text-black font-semibold rounded-full px-2.5 py-1.5 leading-none tracking-wider text-[0.65rem]"
							:class="{
								'border-black/30': !selectedLot?.status || (selectedLot?.status !== 'sold' && selectedLot?.status !== 'underOffer' && selectedLot?.status !== 'available'),
								'bg-[#E85B51] text-black': selectedLot?.status === 'sold',
								'bg-[#FFA54E] text-black': selectedLot?.status === 'underOffer',
								'bg-[#B9CEA4] text-black': selectedLot?.status === 'available'
							}"
						>
							<template
								v-if="selectedLot?.__typename === 'houseLand_Entry'
									&& selectedLot?.status === 'available'
									&& selectedLot?.hasTitle"
							>
								Titles Issued
							</template>
							<template
								v-else-if="selectedLot?.__typename === 'houseLand_Entry'
									&& selectedLot?.status === 'available'
									&& !selectedLot?.hasTitle"
							>
								For Sale
							</template>
							<template
								v-else-if="selectedLot?.__typename !== 'houseLand_Entry'
									&& selectedLot?.status === 'available'"
							>
								For Sale
							</template>
							<template v-else>
								{{ selectedLot?.statusLabel }}
							</template>
						</div>
					</div>
				</div>
				<div v-if="selectedLot?.status === 'no-entry' || isEditMode" class="py-4">
					<LotEntryForm
						:available-stages="availableStages"
						:model-value="{ stageId, status, sectionSize, price, sectionType, enabled, postDate }"
						:is-editing="isEditMode"
						@update:stageId="handleStageIdUpdate"
						@update:status="status = $event"
						@update:sectionSize="sectionSize = $event"
						@update:price="price = $event"
						@update:sectionType="sectionType = $event"
						@update:enabled="enabled = $event"
						@update:postDate="postDate = $event"
					/>
				</div>
				<div v-else-if="!isEditMode">
					<div class="flex items-center gap-1" v-if="selectedLot?.stage && Array.isArray(selectedLot.stage) && selectedLot.stage.length > 0">
						<svg xmlns="http://www.w3.org/2000/svg" width="8.376" height="11.966" viewBox="0 0 8.376 11.966">
						<path id="Path_8204" data-name="Path 8204" d="M393.188-1446a4.185,4.185,0,0,0-4.188,4.188c0,3.141,4.188,7.778,4.188,7.778s4.188-4.637,4.188-7.778a4.185,4.185,0,0,0-4.188-4.188Zm0,5.684a1.5,1.5,0,0,1-1.5-1.5,1.5,1.5,0,0,1,1.5-1.5,1.5,1.5,0,0,1,1.5,1.5,1.5,1.5,0,0,1-1.5,1.5Z" transform="translate(-389 1446)" fill="#0c0c0c" fill-rule="evenodd"/>
						</svg>
						<div class="text-sm">Stage {{ selectedLot.stage[0].title }}</div>
					</div>
				</div>
				<!-- info bar, for sizes and other house info -->
				<div v-if="selectedLot?.sectionSize || selectedLot?.floorSize"
					class="w-full rounded-[0.25rem] bg-[#AAAAAA]/10 border border-[#AAAAAA]/30 border-solid py-2 px-3 flex items-center justify-start flex-wrap gap-x-4 gap-y-2 !mt-3 !mb-8">
					
					<!-- section and floor size -->
					<div v-if="selectedLot?.sectionSize || selectedLot?.floorSize" class="inline-flex items-center justify-start">
						<svg xmlns="http://www.w3.org/2000/svg" width="16.483" height="16.5" viewBox="0 0 16.483 16.5" class="mr-2">
							<g data-name="Group 6223" transform="translate(-2.6)">
								<path data-name="Path 7911" d="M18.926,16.582l-1.6-1.6a.528.528,0,1,0-.747.747l.695.695H5.24V4.406l.695.695a.545.545,0,0,0,.764,0,.524.524,0,0,0,0-.747l-1.6-1.6a.524.524,0,0,0-.747,0l-1.6,1.6A.528.528,0,0,0,3.5,5.1L4.2,4.406V16.964a.541.541,0,0,0,.538.538H17.259l-.695.695a.524.524,0,0,0,0,.747.545.545,0,0,0,.764,0l1.6-1.6A.545.545,0,0,0,18.926,16.582Z" transform="translate(0 -2.6)"/>
								<path data-name="Path 7912" d="M34.538,7.077h.538a.538.538,0,1,0,0-1.077h-.538a.538.538,0,0,0,0,1.077Z" transform="translate(-25.946 -5.409)"/>
								<path data-name="Path 7913" d="M67.738,7.077h.868a.538.538,0,0,0,0-1.077h-.868a.538.538,0,1,0,0,1.077Z" transform="translate(-53.38 -5.409)"/>
								<path data-name="Path 7914" d="M50.138,7.077h.868a.538.538,0,0,0,0-1.077h-.868a.538.538,0,0,0,0,1.077Z" transform="translate(-38.837 -5.409)"/>
								<path data-name="Path 7915" d="M85.238,7.077a.538.538,0,1,0,1.077,0V6.538A.541.541,0,0,0,85.777,6h-.538a.541.541,0,0,0-.538.538A.553.553,0,0,0,85.238,7.077Z" transform="translate(-67.84 -5.409)"/>
								<path data-name="Path 7916" d="M88.877,39.738a.538.538,0,0,0-1.077,0v.868a.538.538,0,0,0,1.077,0Z" transform="translate(-70.402 -32.843)"/>
								<path data-name="Path 7917" d="M88.877,22.138a.538.538,0,0,0-1.077,0v.868a.538.538,0,0,0,1.077,0Z" transform="translate(-70.402 -18.3)"/>
								<path data-name="Path 7918" d="M88.338,56.8a.541.541,0,0,0-.538.538v.538a.538.538,0,0,0,1.077,0v-.538A.53.53,0,0,0,88.338,56.8Z" transform="translate(-70.402 -47.386)"/>
							</g>
						</svg>
						<div v-if="selectedLot?.floorSize"
							class="text-xs mr-2 inline-flex items-center">
							{{ selectedLot.floorSize }}m²
							<div class="w-px h-4 bg-[#2F412F] inline-block ml-2"></div>
						</div>
						<div v-if="selectedLot?.sectionSize"
							class="text-xs">
							{{ selectedLot.sectionSize }}m²
						</div>
					</div>

					<!--living rooms-->
					<div v-if="selectedLot?.livingRooms" class="inline-flex items-center">
						<svg xmlns="http://www.w3.org/2000/svg" width="24" viewBox="0 0 32.492 21.99"><path d="M77,56.005h1.048V45H77V56.005Z" transform="translate(-46.604 -36.636)" fill="none"/><path d="M21,45V56.005h1.048V45Z" transform="translate(-19.952 -36.636)" fill="none"/><rect width="26.203" height="1.048" transform="translate(3.144 16.749)" fill="none"/><rect width="1.09" height="3.333" transform="translate(15.68 8.175)" fill="none"/><path d="M37.053,51.147A1.672,1.672,0,0,0,36.383,51H26.719a1.687,1.687,0,0,0-.671.136A1.735,1.735,0,0,0,25,52.708v2.484H37.578V51.524l-.152-.147A2.017,2.017,0,0,0,37.053,51.147Z" transform="translate(-21.856 -39.492)" fill="none"/><path d="M61.829,31H51.777a.7.7,0,0,0-.7.671v9.04a3.145,3.145,0,0,1,.482-.183,2.84,2.84,0,0,1,.671-.094H61.9a2.8,2.8,0,0,1,.629.084V31.671A.7.7,0,0,0,61.829,31Z" transform="translate(-34.268 -29.973)" fill="none"/><path d="M62.529,51.136A1.687,1.687,0,0,0,61.859,51H52.195a1.672,1.672,0,0,0-.671.147,2.018,2.018,0,0,0-.372.215L51,51.524v3.668H63.578V52.719a1.735,1.735,0,0,0-1.048-1.583Z" transform="translate(-34.23 -39.492)" fill="none"/><path d="M48.348,33.212V30.738a2.048,2.048,0,0,0,0-.252,1.876,1.876,0,0,0-.168-.524.911.911,0,0,0-.052-.1c-.031-.052-.068-.1-.11-.162a1.817,1.817,0,0,0-.131-.152l-.063-.058a1.252,1.252,0,0,0-.183-.147l-.058-.042a1.991,1.991,0,0,0-.257-.136l-.094-.031a.792.792,0,0,1-.2-.063h-.084a1.614,1.614,0,0,0-.32-.031H36.509a1.808,1.808,0,0,0-1.263.5,1.541,1.541,0,0,0-.231-.183,1.05,1.05,0,0,0-.1-.063l-.162-.084a.834.834,0,0,0-.141-.063l-.21-.063h-.094a2.144,2.144,0,0,0-.325-.031H23.931a1.656,1.656,0,0,0-.32.031h-.1a.552.552,0,0,0-.2.063l-.094.031a1.991,1.991,0,0,0-.257.136l-.058.042a1.252,1.252,0,0,0-.183.147l-.084.079-.058.058-.073.094c-.042.058-.079.11-.11.162a.912.912,0,0,0-.052.1,1.305,1.305,0,0,0-.11.267,1.9,1.9,0,0,0-.058.236,1.483,1.483,0,0,0-.026.252v5.6H19v13.1h.786V51.03h1.572V49.457h.786V47.885h26.2v1.572h.786V51.03h1.572V49.457h.786v-13.1H48.348Zm0,9.69v1.839H35.77V41.072l.152-.147a2.018,2.018,0,0,1,.372-.215,1.672,1.672,0,0,1,.671-.162h9.664a1.688,1.688,0,0,1,.671.136,1.735,1.735,0,0,1,1.048,1.572ZM23.193,40.685a1.687,1.687,0,0,1,.671-.136h9.664a1.672,1.672,0,0,1,.671.147,2.017,2.017,0,0,1,.372.215l.152.162v3.668H22.144V42.267a1.735,1.735,0,0,1,1.048-1.583ZM36.53,30.067H46.56a.7.7,0,0,1,.7.671v8.846a2.8,2.8,0,0,0-.629-.084H36.965a2.841,2.841,0,0,0-.671.094,3.145,3.145,0,0,0-.482.183v-9.04a.671.671,0,0,1,.278-.524,1.2,1.2,0,0,1,.162-.084h.084a.262.262,0,0,1,.068,0h.1Zm-13.337.671a.713.713,0,0,1,.173-.424.739.739,0,0,1,.4-.246H34.083a.319.319,0,0,1,.068,0h.084a.718.718,0,0,1,.262.162.681.681,0,0,1,.225.362,1.074,1.074,0,0,0,0,.147v9.04h0a3.144,3.144,0,0,0-.482-.183,2.84,2.84,0,0,0-.713-.094H23.863a2.8,2.8,0,0,0-.629.084h-.042Zm12.053,9.381a2.149,2.149,0,0,0-.262-.183,2.148,2.148,0,0,1,.262.183ZM20.048,37.4h2.1v2.715A2.751,2.751,0,0,0,21.1,42.267v6.142H20.048Zm28.3,9.433h-26.2V45.789h26.2ZM49.4,37.4h1.048V48.409H49.4V42.267a2.751,2.751,0,0,0-1.048-2.149h0V37.4Z" transform="translate(-19 -29.04)"/></svg>
						<div class="text-xs ml-2">
							{{ selectedLot.livingRooms }}
						</div>
					</div>

					<!--bedrooms-->
					<div v-if="selectedLot?.bedrooms" class="inline-flex items-center">
						<svg xmlns="http://www.w3.org/2000/svg" width="30" viewBox="0 0 43.044 21.99"><g transform="translate(0 0)"><path d="M16.445,40.042a6.81,6.81,0,0,0-2.714-3.275A6.023,6.023,0,0,0,9.1,35.6a5.194,5.194,0,0,0-1.825.749V29H4V50.99H7.275V48.65H43.769V50.99h3.275V48.65h0V40.7H16.4A1.5,1.5,0,0,0,16.445,40.042ZM6.339,50.054h-1.4V29.936h1.4Zm.936-12.632a4.5,4.5,0,0,1,2.012-.983,5.114,5.114,0,0,1,3.93,1.029,6.389,6.389,0,0,1,2.293,2.667.614.614,0,0,1-.047.421.355.355,0,0,1-.187.094h-2.9L7.883,39.106a1.78,1.78,0,0,1-.608-.421Zm0,2.526.281.14,1.871.655H7.275ZM46.108,50.054H44.7v-1.4h1.4Zm0-2.339H7.275v-1.4H46.108Zm0-6.082v3.743H7.275V41.632Z" transform="translate(-4 -29)"/></g></svg>
						<div class="text-xs ml-2">
							{{ selectedLot.bedrooms }}
						</div>
					</div>

					<!--bathrooms-->
					<div v-if="selectedLot?.bathrooms" class="inline-flex items-center">
						<svg xmlns="http://www.w3.org/2000/svg" width="24" viewBox="0 0 29.428 23.707">
							<path d="M30.388,8.569a.416.416,0,0,0-.141-.31l-3.189-2.78a1.344,1.344,0,0,0-1.169-.3L22.679,5.9l-.565-.566a1.618,1.618,0,0,0-1.154-.478h0a1.626,1.626,0,0,0-1.155.478l-.554.553a5.811,5.811,0,0,0-5.9,1.322l-.337.336a1.218,1.218,0,0,0-1.4.195,1.2,1.2,0,0,0,0,1.7L20.273,18.1a1.184,1.184,0,0,0,.845.35h0a1.174,1.174,0,0,0,1.047-1.752l.332-.332a5.812,5.812,0,0,0,1.32-5.907l.553-.553a1.642,1.642,0,0,0,.368-1.746l1.073-.241,3.9,3.393A.411.411,0,0,0,30.389,11Zm-9,8.944a.377.377,0,0,1-.531,0L12.2,8.856a.383.383,0,0,1-.11-.267.367.367,0,0,1,.109-.264.375.375,0,0,1,.267-.11.37.37,0,0,1,.265.11l8.659,8.658a.378.378,0,0,1,.109.265A.365.365,0,0,1,21.386,17.513Zm2.4-8.193-.732.733a.417.417,0,0,0-.1.43,5.023,5.023,0,0,1-1.043,5.3l-.282.283L13.645,8.079l.284-.283a5.028,5.028,0,0,1,5.3-1.046.415.415,0,0,0,.431-.1l.732-.733a.8.8,0,0,1,.573-.236h0a.806.806,0,0,1,.571.236l2.254,2.254s0,0,0,.006a.8.8,0,0,1,0,1.139ZM26.2,7.157a.417.417,0,0,0-.361-.092l-1.628.366-.841-.84,2.7-.609a.515.515,0,0,1,.447.115l3.048,2.659,0,1.334Z" transform="translate(-0.961 -4.859)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(12.671 9.995)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(10.54 12.125)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(8.41 14.256)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(6.28 16.386)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(10.486 7.809)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(7.96 9.451)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(5.434 11.092)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(2.811 12.733)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(8.398 5.72)"/>
							<circle cx="0.52" cy="0.52" r="0.52" transform="translate(5.598 6.833)"/>
							<circle cx="0.52" cy="0.52" r="0.52" transform="translate(2.706 7.947)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(0 9.063)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(14.854 12.179)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(13.214 14.707)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(11.574 17.234)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(9.933 19.857)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(16.944 14.269)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(15.829 17.069)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(14.717 19.962)"/>
							<circle cx="0.519" cy="0.519" r="0.519" transform="translate(13.603 22.669)"/></svg>
						<div class="text-xs ml-2">
							{{ selectedLot.bathrooms }}
						</div>
					</div>

					<!-- toilets -->
					 <div v-if="selectedLot?.toilets" class="inline-flex items-center">
						<svg xmlns="http://www.w3.org/2000/svg" width="12" viewBox="0 0 15.162 22.742"><g transform="translate(0)"><path d="M100.49,11.371a1.46,1.46,0,0,0-1.419-1.419V1.421a.948.948,0,0,0-.947-.948H94.807A.474.474,0,0,0,94.333,0H91.49a.473.473,0,0,0-.473.474H87.7a.949.949,0,0,0-.948.948v8.53a1.46,1.46,0,0,0-1.425,1.42,1.385,1.385,0,0,0,.479,1.04,6.487,6.487,0,0,0,2.088,4.508,7.608,7.608,0,0,0,.754.631v4.245h-.006a.474.474,0,1,0,0,.948h8.534a.474.474,0,0,0,0-.947V17.55a7.522,7.522,0,0,0,.754-.631,6.489,6.489,0,0,0,2.088-4.514A1.385,1.385,0,0,0,100.49,11.371ZM87.7,1.421H98.124V9.95H87.7ZM86.829,10.9H98.99a.479.479,0,1,1,0,.947H86.829a.479.479,0,1,1,0-.947Zm9.4,10.9H89.6V18.14a7.186,7.186,0,0,0,2.843.8v1.911a.474.474,0,0,0,.947,0V18.936a7.179,7.179,0,0,0,2.843-.8Zm2.819-9a5.654,5.654,0,0,1-1.772,3.441,6.26,6.26,0,0,1-8.726,0,5.65,5.65,0,0,1-1.771-3.441H99.047Z" transform="translate(-85.328)" fill="#293b29"/><path d="M149.88,42.664a.474.474,0,0,0-.473.474v6.633a.474.474,0,1,0,.947,0V43.138A.474.474,0,0,0,149.88,42.664Z" transform="translate(-146.561 -40.769)"/></g></svg>
						<div class="text-xs ml-2">
							{{ selectedLot.toilets }}
						</div>
					</div>

					<!--cars-->
					<div v-if="selectedLot?.cars" class="inline-flex items-center">
						<svg xmlns="http://www.w3.org/2000/svg" width="18" viewBox="0 0 26.99 19.357">
							<g transform="translate(0 0)">
								<path d="M33.516,23.816a1.461,1.461,0,0,0-.763-2.71h-.587a1.443,1.443,0,0,0-.633.153A6.42,6.42,0,0,0,26.008,17H18.969a6.42,6.42,0,0,0-5.526,4.259,1.443,1.443,0,0,0-.633-.153h-.587a1.461,1.461,0,0,0-.763,2.71A5.5,5.5,0,0,0,9,28.38v3.748a.586.586,0,0,0,0,.123V35.77a.587.587,0,0,0,.587.587h4.693a.587.587,0,0,0,.587-.587V34.6H30.114V35.77a.587.587,0,0,0,.587.587h4.693a.587.587,0,0,0,.587-.587V32.251a.552.552,0,0,0,0-.094,1.514,1.514,0,0,0,0-.235V28.4a5.5,5.5,0,0,0-2.464-4.587Zm-1.349-1.537h.587a.293.293,0,1,1,0,.587h-.587a.293.293,0,0,1,0-.587Zm-17.7,0c0-1.842,2.481-4.106,4.5-4.106h7.039c2.018,0,4.5,2.264,4.5,4.106v.587H14.47Zm-2.247,0h.587a.293.293,0,1,1,0,.587h-.587a.293.293,0,0,1,0-.587Zm2.381,1.76H30.372a4.393,4.393,0,0,1,4.434,4.341v.352H31.094a.587.587,0,0,0,0,1.173h3.713v2.018a1.59,1.59,0,0,1,0,.235,1.537,1.537,0,0,1-1.543,1.267H11.714a1.419,1.419,0,0,1-.276,0,1.525,1.525,0,0,1-1.267-1.267,1.59,1.59,0,0,1,0-.235V30h3.713a.587.587,0,1,0,0-1.173H10.171V28.38a4.393,4.393,0,0,1,4.434-4.341ZM13.69,35.184H10.171V34.128a.757.757,0,0,0,.094.053l.147.088.252.117.153.059a2.4,2.4,0,0,0,.293.082h.147a2.885,2.885,0,0,0,.458.041H13.69Zm21.117,0H31.287V34.6h1.977a2.886,2.886,0,0,0,.458-.041h.147a2.4,2.4,0,0,0,.293-.082l.158-.059.246-.117.147-.088.094-.082ZM17.4,32.057H27.574a1.959,1.959,0,0,0,0-3.912H17.4a1.959,1.959,0,0,0,0,3.912Zm0-2.739H27.574a.786.786,0,0,1,0,1.566H17.4a.786.786,0,0,1,0-1.566Z" transform="translate(-8.994 -17)"/>
							</g>
						</svg>
						<div class="text-xs ml-2">
							{{ selectedLot.cars }}
						</div>
					</div>
				</div>
				<a
					v-if="selectedLot?.status !== 'no-entry' && !isEditMode"  
					:href="selectedLot.url"
					class="group w-full rounded-full border border-[#74895E] border-solid bg-transparent hover:bg-[#74895E] py-4 px-6 flex items-center justify-center gap-1.5 transition-all duration-150 ease-in-out">
					<!-- <svg xmlns="http://www.w3.org/2000/svg" class="transition-all duration-150 ease-in-out text-[#74895E] group-hover:text-black" width="12" height="12" viewBox="0 0 12 12">
						<path id="download_24dp_5F6368_FILL0_wght400_GRAD0_opsz24" d="M166-791l-3.75-3.75,1.05-1.087,1.95,1.95V-800h1.5v6.112l1.95-1.95,1.05,1.087Zm-4.5,3a1.444,1.444,0,0,1-1.059-.441A1.444,1.444,0,0,1,160-789.5v-2.25h1.5v2.25h9v-2.25H172v2.25a1.444,1.444,0,0,1-.441,1.059A1.444,1.444,0,0,1,170.5-788Z" transform="translate(-160 800)" fill="currentColor"/>
					</svg> -->
					<div class="text-inter text-sm leading-none tracking-wider whitespace-nowrap uppercase font-semibold text-black">View Details</div>
				</a>
			</div>
			<div v-if="isAdmin" class="flex flex-col gap-4 mt-4" data-popup-actions>
				<div 
					class="w-full flex items-center justify-start gap-4"
					v-if="isAdmin && selectedLot?.status !== 'no-entry' && !isEditMode">
					<a 
						v-if="cmsEditUrl" 
						:href="cmsEditUrl" 
						class="popup-button popup-button--admin" 
						target="_blank"
					>
						Edit in CMS
					</a>
					<div 
						@click.stop="startEdit" 
						class="popup-button popup-button--admin"
					>
						Edit
					</div>
				</div>
				<div v-if="isAdmin" class="w-full flex items-center justify-start gap-4">
					<div 
						v-if="(selectedLot?.status === 'no-entry' || isEditMode)" 
						@click.stop="isEditMode ? updateEntry() : createEntry()" 
						:disabled="isCreatingEntry || isUpdatingEntry"
						class="popup-button popup-button--create"
					>
						{{ isCreatingEntry ? 'Creating...' : isUpdatingEntry ? 'Updating...' : isEditMode ? 'Update Entry' : 'Create Entry' }}
					</div>
					<div 
						v-if="isEditMode" 
						@click.stop="cancelEdit" 
						class="popup-button"
					>
						Cancel
					</div>
				</div>
				<div v-if="selectedLot?.enabled !== false && selectedLot?.entryStatus === 'pending' && selectedLot?.postDate" class="text-xs text-[#767676]">
					Will be posted on {{ formatPostDateText(selectedLot.postDate) }}
				</div>
			</div>
		</div>

		<!-- Bulk create popup -->
		<BulkLotEntryPopup
			v-if="showBulkPopup"
			:available-stages="availableStages"
			:unlinked-lots="bulkUnlinkedLots"
			@close="closeBulkPopup"
			@completed="handleBulkCompleted"
		/>

		<!-- Bulk screenshot modal -->
		<ScreenshotBulkModal
			v-if="showScreenshotModal"
			:entries="sectionsEntriesRef"
			@close="closeScreenshotModal"
		/>

		<div
			data-map-key
			v-show="!isScreenshotMode && (!isMobile || !showPopup)"
			class="absolute bottom-0 left-0 z-40 flex flex-col w-full sm:w-[19rem] bg-white p-3.5 shadow-lg transition-all">

			<!-- Lot search (mobile only, full map page only) -->
			<form
				v-if="isNarrowLegendViewport && !isEmbedded"
				class="-mx-3.5 -mt-3.5 mb-1 flex items-center border-b border-[#DFDFDF] px-3.5 h-[48px] gap-2"
				@submit.prevent="selectLotByNumber(lotSearchQuery); lotSearchQuery = ''"
			>
				<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="shrink-0 text-black">
					<circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
				</svg>
				<input
					v-model="lotSearchQuery"
					type="text"
					inputmode="numeric"
					placeholder="Search by lot number"
					class="flex-1 text-sm bg-transparent outline-none placeholder-[#9E9E9E]"
				/>
				<button
					v-if="lotSearchQuery"
					type="button"
					class="shrink-0 text-[#9E9E9E] hover:text-black"
					@click="lotSearchQuery = ''"
					aria-label="Clear search"
				>
					<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
						<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
					</svg>
				</button>
				<button
					v-if="lotSearchQuery"
					type="submit"
					class="shrink-0 flex items-center justify-center w-7 h-7 rounded-full bg-primary-one text-primary-four"
					aria-label="Search"
				>
					<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
						<line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
					</svg>
				</button>
			</form>

			<!-- Stages Panel -->
			<div v-if="isStagesPanelReady && isSingleStageMobileMode" class="flex flex-col gap-3.5">
				<div class="flex flex-col gap-3.5 -mx-3.5 px-3.5 border-b border-[#DFDFDF] pt-2 pb-3">
					<div
						v-for="stage in keyStages"
						:key="`single-mobile-${stage.title}`"
						class="flex flex-row-reverse gap-3 relative justify-between"
					>
						<button
							class="w-[30px] h-[30px] rounded-sm bg-[#E9F3C9] flex items-center justify-center cursor-pointer"
							@click="zoomToStage(stage.title)"
							aria-label="Zoom to stage"
						>
							<svg xmlns="http://www.w3.org/2000/svg" width="11.169" height="11.169" viewBox="0 0 11.169 11.169">
							<g id="Group_21218" data-name="Group 21218" transform="translate(-4.979 -5.071)">
							<path id="my_location_24dp_E3E3E3_FILL0_wght300_GRAD0_opsz24" d="M76.942-877.331v-.905a4.41,4.41,0,0,1-2.752-1.285,4.409,4.409,0,0,1-1.285-2.752H72v-.785h.905a4.409,4.409,0,0,1,1.285-2.752,4.41,4.41,0,0,1,2.752-1.285V-888h.785v.905a4.41,4.41,0,0,1,2.752,1.285,4.409,4.409,0,0,1,1.285,2.752h.905v.785h-.905a4.409,4.409,0,0,1-1.285,2.752,4.41,4.41,0,0,1-2.752,1.285v.905ZM77.335-879a3.527,3.527,0,0,0,2.589-1.072A3.528,3.528,0,0,0,81-882.665a3.527,3.527,0,0,0-1.072-2.589,3.527,3.527,0,0,0-2.589-1.072,3.527,3.527,0,0,0-2.589,1.072,3.527,3.527,0,0,0-1.072,2.589,3.528,3.528,0,0,0,1.072,2.589A3.527,3.527,0,0,0,77.335-879Zm0-1.83a1.762,1.762,0,0,1-1.293-.538,1.762,1.762,0,0,1-.538-1.293,1.762,1.762,0,0,1,.538-1.293,1.762,1.762,0,0,1,1.293-.538,1.762,1.762,0,0,1,1.293.538,1.762,1.762,0,0,1,.538,1.293,1.762,1.762,0,0,1-.538,1.293A1.762,1.762,0,0,1,77.335-880.835Zm0-.785a1.007,1.007,0,0,0,.739-.307,1.007,1.007,0,0,0,.307-.739,1.007,1.007,0,0,0-.307-.739,1.007,1.007,0,0,0-.739-.307,1.007,1.007,0,0,0-.739.307,1.007,1.007,0,0,0-.307.739,1.007,1.007,0,0,0,.307.739A1.007,1.007,0,0,0,77.335-881.619ZM77.335-882.665Z" transform="translate(-66.771 893.321)" stroke="##74895E" stroke-width="0.5"/>
							</g>
							</svg>
						</button>

						<div v-if="!isLotFocusedMode" class="flex items-center gap-1.5">
							<button
								class="transition-all duration-150 ease-in-out rounded-full bg-transparent border border-[#D0D0D0] border-solid hover:border-[#E9F3C9] hover:bg-[#E9F3C9] py-2 px-2.5 flex items-center justify-center gap-1.5"
								@click="beginPriceListDownload(stage.title)"
								:disabled="downloadingPriceList === stage.title">
								<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12">
									<path id="download_24dp_5F6368_FILL0_wght400_GRAD0_opsz24" d="M166-791l-3.75-3.75,1.05-1.087,1.95,1.95V-800h1.5v6.112l1.95-1.95,1.05,1.087Zm-4.5,3a1.444,1.444,0,0,1-1.059-.441A1.444,1.444,0,0,1,160-789.5v-2.25h1.5v2.25h9v-2.25H172v2.25a1.444,1.444,0,0,1-.441,1.059A1.444,1.444,0,0,1,170.5-788Z" transform="translate(-160 800)" fill="#74895E"/>
								</svg>
								<div class="text-[0.688rem] leading-none tracking-wider whitespace-nowrap uppercase font-semibold text-black">
									{{ downloadingPriceList === stage.title ? 'Loading...' : 'PRICE LIST' }}
								</div>
							</button>
							<button
								class="transition-all duration-150 ease-in-out rounded-full bg-transparent border border-[#D0D0D0] border-solid hover:border-[#E9F3C9] hover:bg-[#E9F3C9] py-2 px-2.5 flex items-center justify-center gap-1.5"
								@click="downloadMap(stage.title)"
								:disabled="downloadingStage === stage.title"
							>
								<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12">
								<path id="download_24dp_5F6368_FILL0_wght400_GRAD0_opsz24" d="M166-791l-3.75-3.75,1.05-1.087,1.95,1.95V-800h1.5v6.112l1.95-1.95,1.05,1.087Zm-4.5,3a1.444,1.444,0,0,1-1.059-.441A1.444,1.444,0,0,1,160-789.5v-2.25h1.5v2.25h9v-2.25H172v2.25a1.444,1.444,0,0,1-.441,1.059A1.444,1.444,0,0,1,170.5-788Z" transform="translate(-160 800)" fill="#74895E"/>
								</svg>
								<div class="text-[0.688rem] leading-none tracking-wider whitespace-nowrap uppercase font-semibold text-black">{{ downloadingStage === stage.title ? 'Loading...' : 'STAGE MAP' }}</div>
							</button>
						</div>

						<!-- Lot page: full-width clickable row -->
						<div v-if="isEmbedded && lotFromMount && stageFromMount" class="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-[#DFDFDF] cursor-pointer" @click="embeddedActiveTarget = 'lot'; selectLotByNumber(lotFromMount)">
							<span class="text-base">Lot {{ lotFromMount }}</span>
							<button
								:class="['w-5 h-5 rounded-sm flex items-center justify-center cursor-pointer shrink-0', embeddedActiveTarget === 'lot' ? 'bg-[#E9F3C9]' : 'bg-[#E6E6E6]']"
								@click.stop="embeddedActiveTarget = 'lot'; selectLotByNumber(lotFromMount)"
								aria-label="Zoom to lot"
							>
								<svg xmlns="http://www.w3.org/2000/svg" width="11.169" height="11.169" viewBox="0 0 11.169 11.169">
								<g id="Group_21218" data-name="Group 21218" transform="translate(-4.979 -5.071)">
								<path id="my_location_24dp_E3E3E3_FILL0_wght300_GRAD0_opsz24" d="M76.942-877.331v-.905a4.41,4.41,0,0,1-2.752-1.285,4.409,4.409,0,0,1-1.285-2.752H72v-.785h.905a4.409,4.409,0,0,1,1.285-2.752,4.41,4.41,0,0,1,2.752-1.285V-888h.785v.905a4.41,4.41,0,0,1,2.752,1.285,4.409,4.409,0,0,1,1.285,2.752h.905v.785h-.905a4.409,4.409,0,0,1-1.285,2.752,4.41,4.41,0,0,1-2.752,1.285v.905ZM77.335-879a3.527,3.527,0,0,0,2.589-1.072A3.528,3.528,0,0,0,81-882.665a3.527,3.527,0,0,0-1.072-2.589,3.527,3.527,0,0,0-2.589-1.072,3.527,3.527,0,0,0-2.589,1.072,3.527,3.527,0,0,0-1.072,2.589,3.528,3.528,0,0,0,1.072,2.589A3.527,3.527,0,0,0,77.335-879Zm0-1.83a1.762,1.762,0,0,1-1.293-.538,1.762,1.762,0,0,1-.538-1.293,1.762,1.762,0,0,1,.538-1.293,1.762,1.762,0,0,1,1.293-.538,1.762,1.762,0,0,1,1.293.538,1.762,1.762,0,0,1,.538,1.293,1.762,1.762,0,0,1-.538,1.293A1.762,1.762,0,0,1,77.335-880.835Zm0-.785a1.007,1.007,0,0,0,.739-.307,1.007,1.007,0,0,0,.307-.739,1.007,1.007,0,0,0-.307-.739,1.007,1.007,0,0,0-.739-.307,1.007,1.007,0,0,0-.739.307,1.007,1.007,0,0,0-.307.739,1.007,1.007,0,0,0,.307.739A1.007,1.007,0,0,0,77.335-881.619ZM77.335-882.665Z" transform="translate(-66.771 893.321)" stroke="#000" stroke-width="0.5"/>
								</g>
								</svg>
							</button>
						</div>
						<!-- Map page: original lot row -->
						<div v-else-if="lotFromMount && stageFromMount" class="flex items-center gap-2 mt-2 pt-2 border-t border-[#DFDFDF]">
							<span class="text-base">Lot {{ lotFromMount }}</span>
							<button
								class="w-5 h-5 rounded-sm bg-[#E6E6E6] flex items-center justify-center cursor-pointer"
								@click="selectLotByNumber(lotFromMount)"
								aria-label="Zoom to lot"
							>
								<svg xmlns="http://www.w3.org/2000/svg" width="11.169" height="11.169" viewBox="0 0 11.169 11.169">
								<g id="Group_21218" data-name="Group 21218" transform="translate(-4.979 -5.071)">
								<path id="my_location_24dp_E3E3E3_FILL0_wght300_GRAD0_opsz24" d="M76.942-877.331v-.905a4.41,4.41,0,0,1-2.752-1.285,4.409,4.409,0,0,1-1.285-2.752H72v-.785h.905a4.409,4.409,0,0,1,1.285-2.752,4.41,4.41,0,0,1,2.752-1.285V-888h.785v.905a4.41,4.41,0,0,1,2.752,1.285,4.409,4.409,0,0,1,1.285,2.752h.905v.785h-.905a4.409,4.409,0,0,1-1.285,2.752,4.41,4.41,0,0,1-2.752,1.285v.905ZM77.335-879a3.527,3.527,0,0,0,2.589-1.072A3.528,3.528,0,0,0,81-882.665a3.527,3.527,0,0,0-1.072-2.589,3.527,3.527,0,0,0-2.589-1.072,3.527,3.527,0,0,0-2.589,1.072,3.527,3.527,0,0,0-1.072,2.589,3.528,3.528,0,0,0,1.072,2.589A3.527,3.527,0,0,0,77.335-879Zm0-1.83a1.762,1.762,0,0,1-1.293-.538,1.762,1.762,0,0,1-.538-1.293,1.762,1.762,0,0,1,.538-1.293,1.762,1.762,0,0,1,1.293-.538,1.762,1.762,0,0,1,1.293.538,1.762,1.762,0,0,1,.538,1.293,1.762,1.762,0,0,1-.538,1.293A1.762,1.762,0,0,1,77.335-880.835Zm0-.785a1.007,1.007,0,0,0,.739-.307,1.007,1.007,0,0,0,.307-.739,1.007,1.007,0,0,0-.307-.739,1.007,1.007,0,0,0-.739-.307,1.007,1.007,0,0,0-.739.307,1.007,1.007,0,0,0-.307.739,1.007,1.007,0,0,0,.307.739A1.007,1.007,0,0,0,77.335-881.619ZM77.335-882.665Z" transform="translate(-66.771 893.321)" stroke="#000" stroke-width="0.5"/>
								</g>
								</svg>
							</button>
						</div>
					</div>
				</div>
			</div>
			<div v-else-if="isStagesPanelReady" :class="isEmbedded ? 'flex flex-col gap-3.5 -my-3.5' : 'flex flex-col gap-3.5'">
				<div
					v-if="isNarrowLegendViewport && !isEmbedded"
					class="flex items-center justify-between cursor-pointer py-0 pt-2"
					@click="stagesOpen = !stagesOpen"
				>
					<div class="text-[0.688rem] font-semibold text-black uppercase tracking-wide">STAGES</div>
					<svg xmlns="http://www.w3.org/2000/svg" width="9.876" height="6.098" viewBox="0 0 9.876 6.098"
						class="transition-transform duration-300 shrink-0"
						:class="{ 'rotate-180': stagesOpen }"
					>
						<path data-name="Path 30591" d="M1.16,0,4.938,3.769,8.716,0l1.16,1.16L4.938,6.1,0,1.16Z" fill-rule="evenodd"/>
					</svg>
				</div>
				<div
					class="transition-all duration-300"
					:class="{ 'overflow-hidden': isNarrowLegendViewport && !isEmbedded }"
					:style="isNarrowLegendViewport && !isEmbedded ? { maxHeight: stagesOpen ? '1000px' : '0px' } : {}"
				>
					<div class="flex flex-col gap-3.5">
						<div
							v-for="stage in keyStages"
							:key="`default-${stage.title}`"
							:class="isEmbedded ? 'flex flex-col -mx-3.5' : 'flex flex-col gap-3 rounded-[0.25rem] border border-[#DFDFDF] p-4 relative'"
						>
							<!-- Lot page: full-width clickable row, inline icon, vertically centered -->
							<div v-if="isEmbedded" class="flex items-center justify-between gap-2 cursor-pointer px-3.5 py-4 border-b border-[#DFDFDF]" @click="embeddedActiveTarget = 'stage'; zoomToStage(stage.title)">
								<div class="flex items-end gap-2">
									<div class="text-[1.25rem] leading-none">Stage {{ stage.title }}</div>
									<div v-if="stage.titles" class="text-xs text-[#767676]">Titles due {{ stage.titles }}</div>
								</div>
								<button
									:class="['w-5 h-5 rounded-sm flex items-center justify-center cursor-pointer shrink-0', embeddedActiveTarget === 'stage' ? 'bg-[#E9F3C9]' : 'bg-[#E6E6E6]']"
									@click.stop="embeddedActiveTarget = 'stage'; zoomToStage(stage.title)"
									aria-label="Zoom to stage"
								>
								<svg xmlns="http://www.w3.org/2000/svg" width="11.169" height="11.169" viewBox="0 0 11.169 11.169">
								<g id="Group_21218" data-name="Group 21218" transform="translate(-4.979 -5.071)">
								<path id="my_location_24dp_E3E3E3_FILL0_wght300_GRAD0_opsz24" d="M76.942-877.331v-.905a4.41,4.41,0,0,1-2.752-1.285,4.409,4.409,0,0,1-1.285-2.752H72v-.785h.905a4.409,4.409,0,0,1,1.285-2.752,4.41,4.41,0,0,1,2.752-1.285V-888h.785v.905a4.41,4.41,0,0,1,2.752,1.285,4.409,4.409,0,0,1,1.285,2.752h.905v.785h-.905a4.409,4.409,0,0,1-1.285,2.752,4.41,4.41,0,0,1-2.752,1.285v.905ZM77.335-879a3.527,3.527,0,0,0,2.589-1.072A3.528,3.528,0,0,0,81-882.665a3.527,3.527,0,0,0-1.072-2.589,3.527,3.527,0,0,0-2.589-1.072,3.527,3.527,0,0,0-2.589,1.072,3.527,3.527,0,0,0-1.072,2.589,3.528,3.528,0,0,0,1.072,2.589A3.527,3.527,0,0,0,77.335-879Zm0-1.83a1.762,1.762,0,0,1-1.293-.538,1.762,1.762,0,0,1-.538-1.293,1.762,1.762,0,0,1,.538-1.293,1.762,1.762,0,0,1,1.293-.538,1.762,1.762,0,0,1,1.293.538,1.762,1.762,0,0,1,.538,1.293,1.762,1.762,0,0,1-.538,1.293A1.762,1.762,0,0,1,77.335-880.835Zm0-.785a1.007,1.007,0,0,0,.739-.307,1.007,1.007,0,0,0,.307-.739,1.007,1.007,0,0,0-.307-.739,1.007,1.007,0,0,0-.739-.307,1.007,1.007,0,0,0-.739.307,1.007,1.007,0,0,0-.307.739,1.007,1.007,0,0,0,.307.739A1.007,1.007,0,0,0,77.335-881.619ZM77.335-882.665Z" transform="translate(-66.771 893.321)" stroke="##74895E" stroke-width="0.5"/>
								</g>
								</svg>
								</button>
							</div>
							<!-- Map page: original title + absolute icon -->
							<template v-else>
								<div class="flex items-end gap-2">
									<div class="text-[1.25rem] leading-none">Stage {{ stage.title }}</div>
									<div v-if="stage.titles" class="text-xs text-[#767676]">Titles due {{ stage.titles }}</div>
								</div>
								<button
									class="absolute top-1.5 right-1.5 w-5 h-5 rounded-sm bg-[#E9F3C9] flex items-center justify-center cursor-pointer"
									@click="zoomToStage(stage.title)"
									aria-label="Zoom to stage"
								>
								<svg xmlns="http://www.w3.org/2000/svg" width="11.169" height="11.169" viewBox="0 0 11.169 11.169">
								<g id="Group_21218" data-name="Group 21218" transform="translate(-4.979 -5.071)">
								<path id="my_location_24dp_E3E3E3_FILL0_wght300_GRAD0_opsz24" d="M76.942-877.331v-.905a4.41,4.41,0,0,1-2.752-1.285,4.409,4.409,0,0,1-1.285-2.752H72v-.785h.905a4.409,4.409,0,0,1,1.285-2.752,4.41,4.41,0,0,1,2.752-1.285V-888h.785v.905a4.41,4.41,0,0,1,2.752,1.285,4.409,4.409,0,0,1,1.285,2.752h.905v.785h-.905a4.409,4.409,0,0,1-1.285,2.752,4.41,4.41,0,0,1-2.752,1.285v.905ZM77.335-879a3.527,3.527,0,0,0,2.589-1.072A3.528,3.528,0,0,0,81-882.665a3.527,3.527,0,0,0-1.072-2.589,3.527,3.527,0,0,0-2.589-1.072,3.527,3.527,0,0,0-2.589,1.072,3.527,3.527,0,0,0-1.072,2.589,3.528,3.528,0,0,0,1.072,2.589A3.527,3.527,0,0,0,77.335-879Zm0-1.83a1.762,1.762,0,0,1-1.293-.538,1.762,1.762,0,0,1-.538-1.293,1.762,1.762,0,0,1,.538-1.293,1.762,1.762,0,0,1,1.293-.538,1.762,1.762,0,0,1,1.293.538,1.762,1.762,0,0,1,.538,1.293,1.762,1.762,0,0,1-.538,1.293A1.762,1.762,0,0,1,77.335-880.835Zm0-.785a1.007,1.007,0,0,0,.739-.307,1.007,1.007,0,0,0,.307-.739,1.007,1.007,0,0,0-.307-.739,1.007,1.007,0,0,0-.739-.307,1.007,1.007,0,0,0-.739.307,1.007,1.007,0,0,0-.307.739,1.007,1.007,0,0,0,.307.739A1.007,1.007,0,0,0,77.335-881.619ZM77.335-882.665Z" transform="translate(-66.771 893.321)" stroke="##74895E" stroke-width="0.5"/>
								</g>
								</svg>
								</button>
							</template>

							<div v-if="!isLotFocusedMode" class="flex items-center gap-1.5">
								<button
									class="transition-all duration-150 ease-in-out rounded-full bg-transparent border border-[#D0D0D0] border-solid hover:border-[#E9F3C9] hover:bg-[#E9F3C9] py-2 px-2.5 flex items-center justify-center gap-1.5"
									@click="beginPriceListDownload(stage.title)"
									:disabled="downloadingPriceList === stage.title">
									<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12">
										<path id="download_24dp_5F6368_FILL0_wght400_GRAD0_opsz24" d="M166-791l-3.75-3.75,1.05-1.087,1.95,1.95V-800h1.5v6.112l1.95-1.95,1.05,1.087Zm-4.5,3a1.444,1.444,0,0,1-1.059-.441A1.444,1.444,0,0,1,160-789.5v-2.25h1.5v2.25h9v-2.25H172v2.25a1.444,1.444,0,0,1-.441,1.059A1.444,1.444,0,0,1,170.5-788Z" transform="translate(-160 800)" fill="#74895E"/>
									</svg>
									<div class="text-[0.688rem] leading-none tracking-wider whitespace-nowrap uppercase font-semibold text-black">
										{{ downloadingPriceList === stage.title ? 'Loading...' : 'PRICE LIST' }}
									</div>
								</button>
								<button
									class="transition-all duration-150 ease-in-out rounded-full bg-transparent border border-[#D0D0D0] border-solid hover:border-[#E9F3C9] hover:bg-[#E9F3C9] py-2 px-2.5 flex items-center justify-center gap-1.5"
									@click="downloadMap(stage.title)"
									:disabled="downloadingStage === stage.title"
								>
									<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12">
									<path id="download_24dp_5F6368_FILL0_wght400_GRAD0_opsz24" d="M166-791l-3.75-3.75,1.05-1.087,1.95,1.95V-800h1.5v6.112l1.95-1.95,1.05,1.087Zm-4.5,3a1.444,1.444,0,0,1-1.059-.441A1.444,1.444,0,0,1,160-789.5v-2.25h1.5v2.25h9v-2.25H172v2.25a1.444,1.444,0,0,1-.441,1.059A1.444,1.444,0,0,1,170.5-788Z" transform="translate(-160 800)" fill="#74895E"/>
									</svg>
									<div class="text-[0.688rem] leading-none tracking-wider whitespace-nowrap uppercase font-semibold text-black">{{ downloadingStage === stage.title ? 'Loading...' : 'STAGE MAP' }}</div>
								</button>
							</div>

							<!-- Lot page: full-width clickable row -->
							<div v-if="isEmbedded && lotFromMount && stageFromMount" class="flex items-center justify-between gap-2 px-3.5 py-4 cursor-pointer" @click="embeddedActiveTarget = 'lot'; selectLotByNumber(lotFromMount)">
								<span class="text-[1.25rem] leading-none">Lot {{ lotFromMount }}</span>
								<button
									:class="['w-5 h-5 rounded-sm flex items-center justify-center cursor-pointer shrink-0', embeddedActiveTarget === 'lot' ? 'bg-[#E9F3C9]' : 'bg-[#E6E6E6]']"
									@click.stop="embeddedActiveTarget = 'lot'; selectLotByNumber(lotFromMount)"
									aria-label="Zoom to lot"
								>
									<svg xmlns="http://www.w3.org/2000/svg" width="11.169" height="11.169" viewBox="0 0 11.169 11.169">
									<g id="Group_21218" data-name="Group 21218" transform="translate(-4.979 -5.071)">
									<path id="my_location_24dp_E3E3E3_FILL0_wght300_GRAD0_opsz24" d="M76.942-877.331v-.905a4.41,4.41,0,0,1-2.752-1.285,4.409,4.409,0,0,1-1.285-2.752H72v-.785h.905a4.409,4.409,0,0,1,1.285-2.752,4.41,4.41,0,0,1,2.752-1.285V-888h.785v.905a4.41,4.41,0,0,1,2.752,1.285,4.409,4.409,0,0,1,1.285,2.752h.905v.785h-.905a4.409,4.409,0,0,1-1.285,2.752,4.41,4.41,0,0,1-2.752,1.285v.905ZM77.335-879a3.527,3.527,0,0,0,2.589-1.072A3.528,3.528,0,0,0,81-882.665a3.527,3.527,0,0,0-1.072-2.589,3.527,3.527,0,0,0-2.589-1.072,3.527,3.527,0,0,0-2.589,1.072,3.527,3.527,0,0,0-1.072,2.589,3.528,3.528,0,0,0,1.072,2.589A3.527,3.527,0,0,0,77.335-879Zm0-1.83a1.762,1.762,0,0,1-1.293-.538,1.762,1.762,0,0,1-.538-1.293,1.762,1.762,0,0,1,.538-1.293,1.762,1.762,0,0,1,1.293-.538,1.762,1.762,0,0,1,1.293.538,1.762,1.762,0,0,1,.538,1.293,1.762,1.762,0,0,1-.538,1.293A1.762,1.762,0,0,1,77.335-880.835Zm0-.785a1.007,1.007,0,0,0,.739-.307,1.007,1.007,0,0,0,.307-.739,1.007,1.007,0,0,0-.307-.739,1.007,1.007,0,0,0-.739-.307,1.007,1.007,0,0,0-.739.307,1.007,1.007,0,0,0-.307.739,1.007,1.007,0,0,0,.307.739A1.007,1.007,0,0,0,77.335-881.619ZM77.335-882.665Z" transform="translate(-66.771 893.321)" stroke="#000" stroke-width="0.5"/>
									</g>
									</svg>
								</button>
							</div>
							<!-- Map page: original lot row -->
							<div v-else-if="lotFromMount && stageFromMount" class="flex items-center gap-2 mt-2 pt-2 border-t border-[#DFDFDF]">
								<span class="text-base">Lot {{ lotFromMount }}</span>
								<button
									class="w-5 h-5 rounded-sm bg-[#E6E6E6] flex items-center justify-center cursor-pointer"
									@click="selectLotByNumber(lotFromMount)"
									aria-label="Zoom to lot"
								>
									<svg xmlns="http://www.w3.org/2000/svg" width="11.169" height="11.169" viewBox="0 0 11.169 11.169">
									<g id="Group_21218" data-name="Group 21218" transform="translate(-4.979 -5.071)">
									<path id="my_location_24dp_E3E3E3_FILL0_wght300_GRAD0_opsz24" d="M76.942-877.331v-.905a4.41,4.41,0,0,1-2.752-1.285,4.409,4.409,0,0,1-1.285-2.752H72v-.785h.905a4.409,4.409,0,0,1,1.285-2.752,4.41,4.41,0,0,1,2.752-1.285V-888h.785v.905a4.41,4.41,0,0,1,2.752,1.285,4.409,4.409,0,0,1,1.285,2.752h.905v.785h-.905a4.409,4.409,0,0,1-1.285,2.752,4.41,4.41,0,0,1-2.752,1.285v.905ZM77.335-879a3.527,3.527,0,0,0,2.589-1.072A3.528,3.528,0,0,0,81-882.665a3.527,3.527,0,0,0-1.072-2.589,3.527,3.527,0,0,0-2.589-1.072,3.527,3.527,0,0,0-2.589,1.072,3.527,3.527,0,0,0-1.072,2.589,3.528,3.528,0,0,0,1.072,2.589A3.527,3.527,0,0,0,77.335-879Zm0-1.83a1.762,1.762,0,0,1-1.293-.538,1.762,1.762,0,0,1-.538-1.293,1.762,1.762,0,0,1,.538-1.293,1.762,1.762,0,0,1,1.293-.538,1.762,1.762,0,0,1,1.293.538,1.762,1.762,0,0,1,.538,1.293,1.762,1.762,0,0,1-.538,1.293A1.762,1.762,0,0,1,77.335-880.835Zm0-.785a1.007,1.007,0,0,0,.739-.307,1.007,1.007,0,0,0,.307-.739,1.007,1.007,0,0,0-.307-.739,1.007,1.007,0,0,0-.739-.307,1.007,1.007,0,0,0-.739.307,1.007,1.007,0,0,0-.307.739,1.007,1.007,0,0,0,.307.739A1.007,1.007,0,0,0,77.335-881.619ZM77.335-882.665Z" transform="translate(-66.771 893.321)" stroke="#000" stroke-width="0.5"/>
									</g>
									</svg>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

				<!-- Key Map Legend: collapsible on mobile (collapsed by default), always open on desktop -->
				<div v-if="!isLotFocusedMode" class="flex flex-col gap-1.5 mt-4">
					<div
						v-if="isNarrowLegendViewport"
						class="flex items-center justify-between cursor-pointer py-0"
						@click="keyMapOpen = !keyMapOpen"
					>
						<div class="text-[0.688rem] font-semibold text-black uppercase tracking-wide">MAP KEY</div>
						<svg xmlns="http://www.w3.org/2000/svg" width="9.876" height="6.098" viewBox="0 0 9.876 6.098"
							class="transition-transform duration-300 shrink-0"
							:class="{ 'rotate-180': keyMapOpen }"
						>
							<path data-name="Path 30591" d="M1.16,0,4.938,3.769,8.716,0l1.16,1.16L4.938,6.1,0,1.16Z" fill-rule="evenodd"/>
						</svg>
					</div>
					<div v-else class="text-[0.688rem] font-semibold text-black uppercase tracking-wide">MAP KEY</div>
					<div
						class="transition-all duration-300"
						:class="{ 'overflow-hidden': isNarrowLegendViewport }"
						:style="isNarrowLegendViewport ? { maxHeight: keyMapOpen ? '400px' : '0px' } : {}"
					>
						<div class="grid grid-cols-1 gap-y-2 gap-x-4 rounded-[0.25rem] border border-[#DFDFDF] p-4">
							<div class="inline-flex items-center justify-start">
								<div class="w-[1.042rem] inline-flex items-center justify-center mr-2 mt-[2px]">
									<div class="w-3 h-3 bg-[#B9CEA4]"></div>
								</div>
								<div class="text-[0.813rem] leading-none text-black">For Sale - Sections</div>
							</div>
							<div class="inline-flex items-center justify-start">
								<div class="w-[1.042rem] inline-flex items-center justify-center mr-2 mt-[2px]">
									<div class="w-3 h-3 bg-[#E3D4BD]"></div>
								</div>
								<div class="text-[0.813rem] leading-none text-black">For Sale - Townhouse Superlots</div>
							</div>
							<div class="inline-flex items-center justify-start">
								<div class="w-[1.042rem] inline-flex items-center justify-center mr-2 mt-[2px]">
									<div class="w-3 h-3 bg-[#74895E]"></div>
								</div>
								<div class="text-[0.813rem] leading-none text-black">For Sale - House & Land</div>
							</div>
							<div class="inline-flex items-center justify-start">
								<div class="w-[1.042rem] inline-flex items-center justify-center mr-2 mt-[2px]">
									<div class="w-3 h-3 bg-[#ffa64e]"></div>
								</div>
								<div class="text-[0.813rem] leading-none text-black">Under offer</div>
							</div>
							<div class="inline-flex items-center justify-start">
								<div class="w-[1.042rem] inline-flex items-center justify-center mr-2 mt-[2px]">
									<div class="w-3 h-3 bg-[#e85b52]"></div>
								</div>
								<div class="text-[0.813rem] leading-none text-black">Sold</div>
							</div>
						</div>
					</div>

					<div class="hidden sm:block">
					<div class="flex items-center justify-between cursor-pointer py-2 mt-2" @click="amenitiesOpen = !amenitiesOpen">
						<div class="text-[0.688rem] font-semibold text-black uppercase tracking-wide">AMENITIES</div>
						<svg xmlns="http://www.w3.org/2000/svg" width="9.876" height="6.098" viewBox="0 0 9.876 6.098"
							class="transition-transform duration-300 shrink-0"
							:class="{'rotate-180': amenitiesOpen}"
						>
							<path data-name="Path 30591" d="M1.16,0,4.938,3.769,8.716,0l1.16,1.16L4.938,6.1,0,1.16Z" fill-rule="evenodd"/>
						</svg>
					</div>
					<div class="overflow-hidden transition-all duration-300"
						:style="{ maxHeight: amenitiesOpen ? '200px' : '0px'}">
						<div class="flex items-center justify-start gap-2 flex-wrap bg-[#F5FAE4] rounded-[0.25rem] p-4">
							<button
								v-for="item in [{ key: 'education', label: 'Education' }, { key: 'parks', label: 'Parks' }, { key: 'industrial', label: 'Industrial' }]"
								:key="item.key"
								type="button"
								class="py-1 h-[30px] px-2.5 flex items-center gap-2 rounded-full border transition-colors"
								:class="amenityVisible[item.key] ? 'border-[#27331d] bg-[#27331d]' : 'border-[#D0D0D0] bg-white'"
								@click="toggleAmenity(item.key)"
							>
								<svg v-if="item.key == 'education'" xmlns="http://www.w3.org/2000/svg" width="13.751" height="15" viewBox="0 0 13.751 15" class="text-[#74895e]" :class="{ 'text-[#E9F3C9]': amenityVisible[item.key] }">
								<path id="iconmonstr-school-11" d="M6.051,1.439A1.876,1.876,0,0,1,9.7,1.447,3.988,3.988,0,0,1,12.236,3.2c.467.749.659,1.5.971,3.806a1.586,1.586,0,0,1,1.195,1.1,18.392,18.392,0,0,1,.346,3.143,1.748,1.748,0,0,1-1.176,1.957l-.006,0c-.311,1.924-2.2,1.78-2.829,1.787-.9.006-1.682,0-2.86-.007-.749,0-3.328.008-3.627-.01a2.031,2.031,0,0,1-2.061-1.768A1.689,1.689,0,0,1,1,11.46,20.809,20.809,0,0,1,1.3,8.3,1.6,1.6,0,0,1,2.546,7.008c.294-2.28.5-3.243,1.167-4.117A4.009,4.009,0,0,1,6.051,1.439M4.284,4.454a26.66,26.66,0,0,0-.619,3.7c-.207.013-.419.02-.627.039-.43.046-.478.159-.528.454A19.258,19.258,0,0,0,2.25,11.5c.006.371.127.5.434.554.1.017.581.056.687.066.052,1.507.08,1.617,1.656,1.632.834,0,2.85-.007,2.85-.007,1.648.006,2.181.013,3.039,0,1.426-.014,1.421-.262,1.467-1.629.207-.018.415-.033.622-.057.522-.074.507-.339.49-.869a19.631,19.631,0,0,0-.266-2.622.377.377,0,0,0-.356-.356,7.23,7.23,0,0,0-.782-.064,27.276,27.276,0,0,0-.619-3.657C10.923,2.916,9.544,2.5,7.861,2.5c-1.656,0-3.027.392-3.577,1.955m7.341,6.789a1.648,1.648,0,0,1-1.8,1.874q-1.943.018-3.886,0a1.7,1.7,0,0,1-1.81-1.83V8.744h7.5ZM7.25,9.369H4.75c0,.647-.014,1.294,0,1.941a1.077,1.077,0,0,0,1.2,1.181q1.93.02,3.86,0c.864-.027,1.184-.353,1.193-1.251V9.369H8.5v1.874a.625.625,0,1,1-1.25,0V9.369Zm.279-5.122C6.314,4.34,5.683,4.73,5.521,7.5A.313.313,0,0,1,4.9,7.49a10.137,10.137,0,0,1,.219-1.752A2.368,2.368,0,0,1,6.7,3.747a5.2,5.2,0,0,1,2.343,0,2.3,2.3,0,0,1,1.56,1.9,9.821,9.821,0,0,1,.229,1.885.313.313,0,0,1-.621.006c-.191-1.935-.21-3.2-2.034-3.292L8.6,5.7a.747.747,0,1,1-1.461.016Zm.346,1.246a.373.373,0,1,1-.373.373.374.374,0,0,1,.373-.373M8.995,1.316a1.252,1.252,0,0,0-2.237,0,9.6,9.6,0,0,1,2.237,0" transform="translate(-0.999)" fill="currentColor" fill-rule="evenodd"/>
								</svg>

								<svg v-if="item.key == 'parks'" xmlns="http://www.w3.org/2000/svg" width="13.333" height="16" viewBox="0 0 13.333 16" class="text-[#74895e]" :class="{ 'text-[#E9F3C9]': amenityVisible[item.key] }">
								<path id="iconmonstr-tree-2" d="M9.333,16H8V12.781a3.889,3.889,0,0,1-1.3-.6,3.583,3.583,0,0,1-1.033.15A3.734,3.734,0,0,1,2.8,6.293,2.847,2.847,0,0,1,2.6,5.25,2.792,2.792,0,0,1,4.8,2.5a4.24,4.24,0,0,1,7.737,0,2.791,2.791,0,0,1,2.2,2.746,2.858,2.858,0,0,1-.2,1.043,3.735,3.735,0,0,1-2.869,6.041,3.591,3.591,0,0,1-1.03-.149,3.681,3.681,0,0,1-1.3.605V16Zm-2.3-5.387a1.965,1.965,0,0,0,1.671.923,1.879,1.879,0,0,0,1.6-.923,2.539,2.539,0,0,0,3-.292A2.5,2.5,0,0,0,12.885,6.5a1.579,1.579,0,0,0-1.3-2.745A3.211,3.211,0,0,0,8.667,1.333,3.165,3.165,0,0,0,5.753,3.759a1.81,1.81,0,0,0-1.579.671A1.786,1.786,0,0,0,4.448,6.5a2.531,2.531,0,0,0-.411,3.817,2.459,2.459,0,0,0,3,.292Z" transform="translate(-2)" fill="currentColor" fill-rule="evenodd"/>
								</svg>

								<svg v-if="item.key == 'industrial'" xmlns="http://www.w3.org/2000/svg" width="14.084" height="12.653" viewBox="0 0 14.084 12.653" class="text-[#74895e]" :class="{ 'text-[#E9F3C9]': amenityVisible[item.key] }">
								<path id="garage" d="M5.147,15.453H3V5.432L9.442,4l6.442,1.432V15.453H13.737m-8.59,0h8.59m-8.59,0V12.59m8.59,2.863V12.59M5.147,9.727V6.863h8.59V9.727m-8.59,0h8.59m-8.59,0V12.59m8.59-2.863V12.59m-8.59,0h8.59" transform="translate(-2.4 -3.4)" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2"/>
								</svg>

								<div class="text-[0.813rem] leading-none text-[#293B29]" :class="{'text-[#E9F3C9]' : amenityVisible[item.key]}">{{ item.label }}</div>
							</button>
						</div>
					</div>
					<div class="text-[10px] text-gray-500 mt-1 leading-tight">Note: Illustrative purposes only, subject to change.</div>
					</div>
				</div>
			</div>
		</div>
		
		<PriceListPdf
			v-if="currentPriceListStage && currentPriceListLots.length > 0"
			ref="priceListPdfRef"
			:stage-data="currentPriceListStage"
			:lots="currentPriceListLots"
		/>

		<!-- Embed scroll overlay -->
		<div 
			v-if="!isLotFocusedMode"
			class="embed-scroll-overlay"
			:class="{ 'is-visible': isEmbedded && showScrollOverlay }"
			@click="dismissScrollOverlay"
		>
			<div class="overlay-message">
				<template v-if="isMobile">
					Use two fingers to pan and zoom
				</template>
				<template v-else>
					Use {{ modifierKey }} + scroll to zoom, click and drag to pan
				</template>
			</div>
		</div>

		<div 
			v-if="isLotFocusedMode"
			class="absolute top-0 left-0 w-full h-full cursor-default"
		></div>

		<div data-north class="absolute left-[1.25rem] sm:top-auto sm:bottom-[1.25rem] sm:left-auto sm:right-[1.25rem]" :class="isEmbedded ? 'top-5' : 'top-[calc(var(--header-menu-height)+1.25rem)]'">
			<svg xmlns="http://www.w3.org/2000/svg" width="47" height="47" viewBox="0 0 47 47">
			<g data-name="Group 16254" transform="translate(-379 -517)">
				<rect data-name="Rectangle 11083" width="47" height="47" rx="23.5" transform="translate(379 517)" fill="#fff" opacity="0.95"/>
				<g data-name="Group 21265">
				<path d="M19.591,3.252l-6.6,6.6L6.392,8.2l13.2-4.95ZM22.841,0,0,8.565,11.42,11.42l2.868,11.42Z" transform="matrix(0.966, -0.259, 0.259, 0.966, 387.15, 536.413)" fill="#27331d"/>
				<path data-name="Path 30231" d="M2.107-2.429V-9.968H.889V-7c0,1.144.021,2.287.053,3.431H.921c-.508-1.08-1.048-2.171-1.6-3.24L-2.3-9.968H-4.13v7.539H-2.9V-5.394q0-1.715-.064-3.431h.021c.508,1.08,1.048,2.171,1.6,3.24L.286-2.429Z" transform="translate(398.268 534.93)" fill="#27331d"/>
				</g>
			</g>
			</svg>
		</div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue'
import { useApolloClient } from '@vue/apollo-composable'
import { SVG } from '@svgdotjs/svg.js'
import '@svgdotjs/svg.draggable.js'
import '@svgdotjs/svg.panzoom.js'
import { GET_ALL_SECTIONS, GET_ALL_STAGES, GET_CURRENT_MAP, GET_ALL_MAPS } from '../composables/sitePlanQueries'
import { executeQueryWithCacheAndNetwork } from '../composables/useApollo'
import LotEntryForm from './LotEntryForm.vue'
import BulkLotEntryPopup from './BulkLotEntryPopup.vue'
import ScreenshotBulkModal from './ScreenshotBulkModal.vue'
import PriceListPdf from './PriceListPdf.vue'
import MapSwitcher from './MapSwitcher.vue'
import { isPdfPreviewMode } from '../utils/pdfPreviewMode'

export default {
	name: 'SitePlanMap',
	components: {
		LotEntryForm,
		BulkLotEntryPopup,
		ScreenshotBulkModal,
		PriceListPdf,
		MapSwitcher
	},
	props: {
		svgPath: {
			type: String,
			default: '/dist/images/site-plan.svg'
		},
		embedded: {
			type: [Boolean, String],
			default: false
		}
	},
	setup(props) {
		const isEmbedded = ref(false)
		const isMobile = ref(false)
		const modifierKey = ref('⌘') // mac default

		const { client: apolloClient } = useApolloClient()

		const mapSwitcherRef = ref(null)
		const allMaps = ref([])
		const activeMapId = ref(null)
		const isLoadingMaps = ref(false)
		
		const mapWrapper = ref(null)
		const mapContent = ref(null)
		const priceListPdfRef = ref(null)
		const currentMapSvgUrl = ref('/dist/images/site-plan.svg')
		const currentPriceListStage = ref(null)
		const currentPriceListLots = ref([])
		const downloadingPriceList = ref(null)

		const screenshotOverlayMessage = ref('')
		const screenshotStatus = ref('loading')

		let preventPageZoomFn = null
		let draw = null
		let viewBoxBoundaryZoomRaf = null
		let isPinchZoomActive = false
		let sectionsData = null
		let gestureStartedWithMultipleFingers = false
		let handlersInitialized = false

		let lastLotClickTime = 0
		const lotClickDebounce = 150
		
		// Popup state
		const showPopup = ref(false)
		const selectedLot = ref(null)
		
		// Stages panel state
		const availableStages = ref([])
		const hasLoadedAvailableStages = ref(false)

		// Lot-focused mode: lot/stage passed via data attributes (e.g. listing page)
		const lotFromMount = ref(null)
		const stageFromMount = ref(null)
		const embeddedActiveTarget = ref('lot') // 'lot' | 'stage' — tracks which row is currently targeted on embedded pages
		
		// Admin state
		const isAdmin = ref(false)
		const sectionSize = ref('')
		const price = ref('')
		const stageId = ref('')
		const status = ref('')
		const sectionType = ref('')
		const enabled = ref(true)
		const postDate = ref('')
		const isCreatingEntry = ref(false)
		const isUpdatingEntry = ref(false)
		const isEditMode = ref(false)
		const downloadingStage = ref(null)
		
		// Drag state tracking
		const isDragging = ref(false)
		const isMouseDown = ref(false)
		let lastDragAt = null

		// Bulk popup state
		const showBulkPopup = ref(false)
		const bulkUnlinkedLots = ref([])

		// Bulk screenshot modal state
		const showScreenshotModal = ref(false)
		const sectionsEntriesRef = ref([])

		// Amenities filter visibility (layer ids under #amenities, hidden by default)
		const amenityVisible = ref({ education: false, parks: false, industrial: false })
		// Dropdown open on desktop, closed on mobile (sync with isMobile once it’s set)
		const legendNarrowMql = () =>
			typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
		const isNarrowLegendViewport = ref(legendNarrowMql())
		const amenitiesOpen = ref(!legendNarrowMql())
		const keyMapOpen = ref(false)
		const stagesOpen = ref(!legendNarrowMql())
		const lotSearchQuery = ref('')

		const syncLegendPanelsToViewport = () => {
			const narrow = legendNarrowMql()
			if (narrow === isNarrowLegendViewport.value) return
			isNarrowLegendViewport.value = narrow
			if (narrow) {
				amenitiesOpen.value = false
				keyMapOpen.value = false
				stagesOpen.value = true
			} else {
				amenitiesOpen.value = true
				stagesOpen.value = true
			}
		}
		let legendResizeRaf = null
		const onLegendViewportResize = () => {
			if (legendResizeRaf != null) cancelAnimationFrame(legendResizeRaf)
			legendResizeRaf = requestAnimationFrame(() => {
				legendResizeRaf = null
				syncLegendPanelsToViewport()
			})
		}

		// Expose for lotFilter
		window.selectLotByNumber = selectLotByNumber
		
		const isScreenshotMode = ref(false)

		const showDownloadOverlay = ref(false)
		const downloadStageFromUrl = ref(null)
		const downloadTypeFromUrl = ref(null)
		
		const isLotFocusedMode = computed(() => !!lotFromMount.value)
		const keyStages = computed(() => {
			if (lotFromMount.value && stageFromMount.value) {
				return availableStages.value.filter(s => s.title === stageFromMount.value)
			}
			return availableStages.value
		})
		const isSingleStageMobileMode = computed(() =>
			isNarrowLegendViewport.value && !isEmbedded.value && keyStages.value.length === 1
		)
		const isStagesPanelReady = computed(() =>
			!isNarrowLegendViewport.value || isEmbedded.value || hasLoadedAvailableStages.value
		)

		// Computed property for CMS edit URL
		const cmsEditUrl = computed(() => {
			if (!isAdmin.value || !selectedLot.value?.id) return null
			return `/imagiccms/entries/sectionHouse/${selectedLot.value.id}`
		})

		const dismissScrollOverlay = () => {
			showScrollOverlay.value = false
			if (scrollOverlayTimer.value) {  // ← Check the timer instead
				clearTimeout(scrollOverlayTimer.value)
				scrollOverlayTimer.value = null
			}
		}

		const loadAllMaps = async () => {
			if (!isAdmin.value) return
			
			isLoadingMaps.value = true
			try {
				const result = await executeQueryWithCacheAndNetwork(
				apolloClient,
				GET_ALL_MAPS,
				{},
				true  // force network to get fresh order
				)
				
				if (result?.data?.entries) {
				allMaps.value = result.data.entries.map(entry => ({
					id: entry.id,
					title: entry.title,
					enabled: entry.enabled !== false,
					status: entry.status,
					postDate: entry.postDate || null,
					svgUrl: entry.svgFile?.[0]?.url || null
				}))
				
				// Only set activeMapId if it hasn't been set yet by loadCurrentMap
				if (!activeMapId.value && allMaps.value.length > 0) {
					activeMapId.value = allMaps.value[0].id
				}
				
				console.log('[MapSwitcher] Loaded maps:', allMaps.value)
				}
			} catch (error) {
				console.error('[MapSwitcher] Error loading maps:', error)
			} finally {
				isLoadingMaps.value = false
			}
		}

		const handleMapPreview = async (map) => {
			console.log('[Map Switcher] Previewing map: ', map.title, map.id)

			try {
				// Update the SVG URL and re-initialize
				if (map.svgUrl) {
					currentMapSvgUrl.value = map.svgUrl
				}
				
				// Close any open popup and reset selections
				closePopup()
				resetLotSelections()
				
				// Re-initialize the map with the new SVG
				await initializeMap()
				
				console.log('[MapSwitcher] Preview loaded:', map.title)
			} catch (error) {
				console.error('[MapSwitcher] Error previewing map:', error)
				alert('Error previewing map: ' + error.message)
				mapSwitcherRef.value?.resetPreview()
			}
		}

		const handleSetActive = async (map) => {
			console.log('[MapSwitcher] Setting active:', map.title, map.id)
			
			try {
				const csrfToken = getCsrfToken()
				const response = await fetch('/api/site-plan/switch-map', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrfToken
					},
					body: JSON.stringify({ entryId: map.id, makeActive: true })
				})
				
				const data = await response.json()
				
				if (!data.success) {
					console.error('[MapSwitcher] Backend error:', data.error)
					alert('Failed to set active map: ' + (data.error || 'Unknown error'))
					mapSwitcherRef.value?.resetAfterSetActive(false)
					return
				}
				
				console.log('[MapSwitcher] Backend confirmed:', data)
				
				// Update active map ID
				activeMapId.value = map.id

				// Update the map's local status (it's now live, not pending)
				const mapInList = allMaps.value.find(m => String(m.id) === String(map.id))
				if (mapInList) {
					mapInList.status = 'enabled'
					mapInList.enabled = true
					mapInList.postDate = null
				}
				
				// Reorder local maps array
				const idx = allMaps.value.findIndex(m => String(m.id) === String(map.id))
				if (idx > 0) {
					const [switched] = allMaps.value.splice(idx, 1)
					allMaps.value.unshift(switched)
				}
				
				// Reset switcher UI (success)
				mapSwitcherRef.value?.resetAfterSetActive(true)
				
				console.log('[MapSwitcher] Map set as active:', map.title)
				
			} catch (error) {
				console.error('[MapSwitcher] Error setting active:', error)
				alert('Error setting active map: ' + error.message)
				mapSwitcherRef.value?.resetAfterSetActive(false)
			}
		}

		const handleCancelPreview = async () => {
			console.log('[MapSwitcher] Cancelling preview, reverting to active map')
			
			try {
				// Find the active map's SVG URL
				const activeMap = allMaps.value.find(m => String(m.id) === String(activeMapId.value))
				if (activeMap?.svgUrl) {
					currentMapSvgUrl.value = activeMap.svgUrl
				}
				
				// Close any open popup and reset selections
				closePopup()
				resetLotSelections()
				
				// Re-initialize with the original active map
				await initializeMap()
				
				console.log('[MapSwitcher] Reverted to active map')
			} catch (error) {
				console.error('[MapSwitcher] Error reverting:', error)
			}
		}

		// Format postDate for datetime-local input (ISO string → YYYY-MM-DDTHH:MM)
		const formatPostDateForInput = (postDate) => {
			if (!postDate) return ''
			try {
				const d = new Date(postDate)
				if (isNaN(d.getTime())) return ''
				// Format as YYYY-MM-DDTHH:MM for datetime-local input
				const year = d.getFullYear()
				const month = String(d.getMonth() + 1).padStart(2, '0')
				const day = String(d.getDate()).padStart(2, '0')
				const hours = String(d.getHours()).padStart(2, '0')
				const minutes = String(d.getMinutes()).padStart(2, '0')
				return `${year}-${month}-${day}T${hours}:${minutes}`
			} catch {
				return ''
			}
		}
		const showScrollOverlay = ref(false)
		const scrollOverlayTimer = ref(null)

		const onWheelGlobal = (event) => {
			if (!isEmbedded.value || isMobile.value) return
			
			const mapEl = mapWrapper.value
			if (!mapEl) return
			const rect = mapEl.getBoundingClientRect()
			const isOverMap = (
				event.clientX >= rect.left &&
				event.clientX <= rect.right &&
				event.clientY >= rect.top &&
				event.clientY <= rect.bottom
			)
			if (!isOverMap) return
			
			const modifierPressed = event.metaKey || event.ctrlKey
			
			if (modifierPressed) {
				showScrollOverlay.value = false
				if (scrollOverlayTimer.value) {
					clearTimeout(scrollOverlayTimer.value)
					scrollOverlayTimer.value = null
				}
				return
			}
			
			showScrollOverlay.value = true
			if (scrollOverlayTimer.value) {
				clearTimeout(scrollOverlayTimer.value)
			}
			scrollOverlayTimer.value = setTimeout(() => {
				showScrollOverlay.value = false
				scrollOverlayTimer.value = null
			}, 2000)
		}

		let touchStartFingers = 0

		const onTouchStartGlobal = (event) => {
			if (!isEmbedded.value || !isMobile.value) return
			
			const mapEl = mapWrapper.value
			if (!mapEl) return
			const touch = event.touches[0]
			const rect = mapEl.getBoundingClientRect()
			const isOverMap = (
				touch.clientX >= rect.left &&
				touch.clientX <= rect.right &&
				touch.clientY >= rect.top &&
				touch.clientY <= rect.bottom
			)
			if (!isOverMap) return
			
			touchStartFingers = event.touches.length

			if (event.touches.length >= 2) {
				gestureStartedWithMultipleFingers = true
			} else {
				gestureStartedWithMultipleFingers = false
			}

			if (event.touches.length === 1) {
				const target = event.target;
				const isLotElement = target.closest('[id^="_"]') || target.getAttribute('id')?.match(/^_\d+(-\d+)?$/)
				if (!isLotElement) {
					if (scrollOverlayTimer.value) clearTimeout(scrollOverlayTimer.value)
						scrollOverlayTimer.value = setTimeout(() => {
							showScrollOverlay.value = true
							// Auto-hide after 2 seconds
							setTimeout(() => {
								showScrollOverlay.value = false
							}, 2000)
						}, 1000)
				}
			} else {
				showScrollOverlay.value = false
				if (scrollOverlayTimer.value) {
					clearTimeout(scrollOverlayTimer.value)
					scrollOverlayTimer.value = null
				}
			}
		}

		const onTouchMoveGlobal = (event) => {
			if (!isEmbedded.value || !isMobile.value) return
			
			if (event.touches.length >= 2) {
				showScrollOverlay.value = false
				if (scrollOverlayTimer.value) {
				clearTimeout(scrollOverlayTimer.value)
				scrollOverlayTimer.value = null
				}
			} else if (event.touches.length === 1) {
				// Only show overlay if gesture STARTED with one finger
				if (!gestureStartedWithMultipleFingers) {
				showScrollOverlay.value = true
				if (scrollOverlayTimer.value) clearTimeout(scrollOverlayTimer.value)
				scrollOverlayTimer.value = setTimeout(() => {
					showScrollOverlay.value = false
					scrollOverlayTimer.value = null
				}, 2000)
				}
			}
		}

		const onTouchEndGlobal = (event) => {
			if (!isEmbedded.value || !isMobile.value) return
			
			// When all fingers lifted, reset flags
			if (event.touches.length === 0) {
				gestureStartedWithMultipleFingers = false
				
				setTimeout(() => {
				isDragging.value = false
				lastDragAt = null
				}, 100)
			}
		}

		onMounted(() => {
			const appEl = document.getElementById('site-plan-app')
			const isEmbeddedAttr = appEl?.hasAttribute('data-embedded')
			isEmbedded.value = window.self !== window.top || props.embedded === true || props.embedded === 'true' || isEmbeddedAttr
			lotFromMount.value = appEl?.getAttribute('data-lot') || null
			stageFromMount.value = appEl?.getAttribute('data-stage') || null
			
			isMobile.value = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
			isNarrowLegendViewport.value = legendNarrowMql()
			amenitiesOpen.value = !isNarrowLegendViewport.value
			if (isNarrowLegendViewport.value) keyMapOpen.value = false

			window.addEventListener('resize', onLegendViewportResize)

			if (!isMobile.value) {
				const userAgent = navigator.userAgent || navigator.platform
				const isMac = /Mac|iPhone|iPod|iPad/i.test(userAgent)
				modifierKey.value = isMac ? '⌘' : 'Ctrl'
			}

			// Listen on WINDOW so we catch events regardless of overlay/map/SVG
			window.addEventListener('wheel', onWheelGlobal, { passive: true, capture: true })
			window.addEventListener('touchstart', onTouchStartGlobal, { passive: true, capture: true })
			window.addEventListener('touchmove', onTouchMoveGlobal, { passive: true, capture: true })
			window.addEventListener('touchend', onTouchEndGlobal, { passive: true, capture: true })

			if (mapWrapper.value) {
				preventPageZoomFn = (e) => {
					if (e.touches && e.touches.length > 1) {
					e.preventDefault()
					}
				}
				
				mapWrapper.value.addEventListener('touchstart', preventPageZoomFn, { passive: false, capture: true })
				mapWrapper.value.addEventListener('touchmove', preventPageZoomFn, { passive: false, capture: true })
			}
		})
		
		// Check admin status
		const checkAdminStatus = async () => {
			try {
				const response = await fetch('/api/site-plan/check-admin')
				const data = await response.json()
				isAdmin.value = data.isAdmin
				// console.log('Admin status checked:', data)
			} catch (error) {
				console.error('Error checking admin status:', error)
				// Default to non-admin on error
				isAdmin.value = false
			}
		}
		
		// Get lot number from URL parameter
		const getLotFromURL = () => {
			const urlParams = new URLSearchParams(window.location.search)
			return urlParams.get('lot')
		}

		// Get lot number from mount element data attribute (passed via include)
		const getLotFromMountElement = () => {
			const el = document.getElementById('site-plan-app')
			return el?.getAttribute('data-lot') || null
		}

		// Get stage from mount element data attribute (passed via include)
		const getStageFromMountElement = () => {
			const el = document.getElementById('site-plan-app')
			return el?.getAttribute('data-stage') || null
		}
		
		// Update URL with lot parameter
		const updateURLWithLot = (lotNumber) => {
    		if (isScreenshotMode.value) return
			
			const url = new URL(window.location)
			if (lotNumber) {
				url.searchParams.set('lot', lotNumber)
			} else {
				url.searchParams.delete('lot')
			}
			window.history.replaceState({}, '', url)
		}
		
		// Get stage from URL parameter
		const getStageFromURL = () => {
			const urlParams = new URLSearchParams(window.location.search)
			return urlParams.get('stage')
		}
		
		// Update URL with stage parameter
		const updateURLWithStage = (stageTitle) => {
			const url = new URL(window.location)
			if (stageTitle) {
				url.searchParams.set('stage', stageTitle)
			} else {
				url.searchParams.delete('stage')
			}
			window.history.replaceState({}, '', url)
		}

		const getDownloadParamsFromUrl = () => {
			const urlParams = new URLSearchParams(window.location.search)
			const download = urlParams.get('download')
			const stage = urlParams.get('stage')
			if (!stage || !download) return null
			if (download !== 'map' && download !== 'pricelist') return null
			return { download, stage }
		}

		const dismissDownloadOverlay = () => {
			showDownloadOverlay.value = false
			downloadStageFromUrl.value = null
			downloadTypeFromUrl.value = null
			const url = new URL(window.location)
			url.searchParams.delete('download')
			url.searchParams.delete('stage')
			window.history.replaceState({}, '', url)
		}
		
		// Extract canonical lot number from element ID (e.g. _1602 or _1602-2 -> 1602)
		const getLotNumberFromId = (id) => {
			if (!id || !id.startsWith('_')) return null
			const match = id.match(/^_(\d+)(?:-\d+)?$/)
			return match ? match[1] : null
		}
		
		// Find lot element by lot number, supporting both _N and _N-X IDs
		const findLotElementByNumber = (lotsGroup, lotNumber) => {
			return lotsGroup.findOne(`#_${lotNumber}`) || lotsGroup.findOne(`[id^="_${lotNumber}-"]`)
		}
		
		// Show/hide lot dimension groups based on selection (null = hide all)
		const updateDimensionsVisibility = (selectedLotNumber) => {
			if (!draw) return
			const lotDimensions = draw.findOne('#lot_dimensions')
			if (!lotDimensions) return
			const groups = lotDimensions.find('[id^="_"]')
			groups.forEach(el => {
				const lotNum = getLotNumberFromId(el.attr('id'))
				if (!lotNum) return
				el.attr('visibility', selectedLotNumber && lotNum === selectedLotNumber ? 'visible' : 'hidden')
			})
		}

		// Show/hide amenity layers under #amenities based on filter state (only looks at direct children of #amenities)
		const updateAmenitiesVisibility = () => {
			if (!draw) return
			const amenities = draw.findOne('#amenities')
			if (!amenities) return
			const state = amenityVisible.value
			Object.keys(state).forEach((key) => {
				const el = amenities.findOne(`#${key}`)
				if (el) el.attr('visibility', state[key] ? 'visible' : 'hidden')
			})
		}

		// Zoom viewBox to fit #stages and all currently visible amenity groups (only under #amenities)
		const zoomToStagesAndAmenities = () => {
			if (!draw) return
			try { draw.timeline().stop() } catch (e) { /* no active timeline */ }
			const stagesGroup = draw.findOne('#stages')
			const amenities = draw.findOne('#amenities')
			let minX = Infinity
			let minY = Infinity
			let maxX = -Infinity
			let maxY = -Infinity
			if (stagesGroup) {
				const b = stagesGroup.bbox()
				minX = Math.min(minX, b.x)
				minY = Math.min(minY, b.y)
				maxX = Math.max(maxX, b.x + b.width)
				maxY = Math.max(maxY, b.y + b.height)
			}
			if (amenities) {
				const state = amenityVisible.value
				Object.keys(state).forEach((key) => {
					if (!state[key]) return
					const el = amenities.findOne(`#${key}`)
					if (el) {
						const b = el.bbox()
						minX = Math.min(minX, b.x)
						minY = Math.min(minY, b.y)
						maxX = Math.max(maxX, b.x + b.width)
						maxY = Math.max(maxY, b.y + b.height)
					}
				})
			}
			if (minX === Infinity || maxX === -Infinity) return
			const padding = 200
			const width = maxX - minX + (padding * 2)
			const height = maxY - minY + (padding * 2)
			const centerX = minX - padding + (width / 2)
			const centerY = minY - padding + (height / 2)
			const currentViewBox = draw.viewbox()
			const scaleX = currentViewBox.width / width
			const scaleY = currentViewBox.height / height
			const scale = Math.min(scaleX, scaleY) * 0.9
			const newWidth = currentViewBox.width / scale
			const newHeight = currentViewBox.height / scale
			const newX = centerX - (newWidth / 2)
			const newY = centerY - (newHeight / 2)
			draw.animate(800, 'ease-in-out').viewbox(newX, newY, newWidth, newHeight)
		}

		const toggleAmenity = (key) => {
			amenityVisible.value = { ...amenityVisible.value, [key]: !amenityVisible.value[key] }
			updateAmenitiesVisibility()
			zoomToStagesAndAmenities()
		}

		// Automatically select lot by number
		const selectLotByNumber = async (lotNumber) => {
			if (!draw || !sectionsData) return false
			
			const lotsGroup = draw.findOne('#lots')
			if (!lotsGroup) {
				// console.log('Lots layer #lots not found')
				return false
			}
			const lotElement = findLotElementByNumber(lotsGroup, lotNumber)
			if (!lotElement) {
				// console.log(`Lot element _${lotNumber} not found`)
				return false
			}
			
			// Reset any existing selections
			resetLotSelections()
			closePopup()
			
			// Select the lot element
			lotElement.addClass('lot-selected')
			lotElement.front()
			updateDimensionsVisibility(lotNumber)
			
			// Find lot data and show popup (skip popup in lot-focused mode)
			const lotData = sectionsData?.data.find(s => s.title === lotNumber)
			if (lotFromMount.value) {
				if (lotData) selectedLot.value = lotData
			} else if (lotData) {
				showLotPopup(lotData, null)
			} else if (isAdmin.value) {
				// For admin users, show popup even if no entry data exists
				const adminLotData = {
				  id: lotNumber,
				  title: lotNumber,
				  price: 0,
				  status: 'no-entry',
				  statusLabel: '',
				  url: '#'
				}
				showLotPopup(adminLotData, null)
			}
			
			// Zoom to the lot
			zoomToLot(lotElement)
			
			// console.log(`Auto-selected lot: ${lotNumber}`)
			return true
		}

		const checkScreenshotMode = async () => {
			if (!isScreenshotMode.value) return
			if (!isAdmin.value) return
			
			const urlParams = new URLSearchParams(window.location.search)
			const lot = urlParams.get('lot')
			if (!lot) return
			
			const remainingStr = urlParams.get('remaining') || ''
			const total = parseInt(urlParams.get('total') || '1', 10)
			const remainingList = remainingStr ? remainingStr.split(',').filter(Boolean) : []
			const currentIndex = total - remainingList.length
			const bulkSuffix = total > 1 ? ` (${currentIndex} of ${total})` : ''
			
			console.log('[Screenshot] Mode active for lot:', lot, bulkSuffix && `bulk ${currentIndex}/${total}`)
			screenshotOverlayMessage.value = 'Focusing on Lot ' + lot + '...' + bulkSuffix
			
			await new Promise(resolve => setTimeout(resolve, 1500))
			
			try { draw.timeline().stop() } catch(e) {}
			
			// Apply screenshot zoom (use same lot lookup as selectLotByNumber for compound ids e.g. _5-0)
			const lotsGroup = draw.findOne('#lots')
			if (lotsGroup) {
				const lotElement = findLotElementByNumber(lotsGroup, lot)
				if (lotElement) {
					const bbox = lotElement.bbox()
					const padding = 60
					const width = bbox.width + (padding * 2)
					const height = bbox.height + (padding * 2)
					const centerX = bbox.x - padding + (width / 2)
					const centerY = bbox.y - padding + (height / 2)
					
					const currentViewBox = draw.viewbox()
					const scaleX = currentViewBox.width / width
					const scaleY = currentViewBox.height / height
					const scale = Math.min(scaleX, scaleY) * 2.4
					
					const newWidth = currentViewBox.width / scale
					const newHeight = currentViewBox.height / scale
					const newX = centerX - (newWidth / 2)
					const newY = centerY - (newHeight / 2)
					
					draw.viewbox(newX, newY, newWidth, newHeight)
				}
			}
			
			// Show dimensions for this lot only (same as when a lot is selected in normal mode)
			updateDimensionsVisibility(lot)
			
			screenshotOverlayMessage.value = 'Generating screenshot...' + bulkSuffix
			await new Promise(resolve => setTimeout(resolve, 1000))
			
			// Re-apply zoom immediately before capture so it is not overridden
			const lotsGroupForCapture = draw.findOne('#lots')
			if (lotsGroupForCapture) {
				const lotElForCapture = findLotElementByNumber(lotsGroupForCapture, lot)
				if (lotElForCapture) {
					const bbox = lotElForCapture.bbox()
					const padding = 60
					const w = bbox.width + (padding * 2)
					const h = bbox.height + (padding * 2)
					const cx = bbox.x - padding + (w / 2)
					const cy = bbox.y - padding + (h / 2)
					const vb = draw.viewbox()
					const scale = Math.min(vb.width / w, vb.height / h) * 2.4
					draw.viewbox(cx - (vb.width / scale) / 2, cy - (vb.height / scale) / 2, vb.width / scale, vb.height / scale)
				}
			}
			updateDimensionsVisibility(lot)
			
			try {
				if (typeof html2canvas === 'undefined') {
					screenshotOverlayMessage.value = 'html2canvas not available'
					return
				}
				
				const canvas = await html2canvas(mapContent.value, {
					scale: 3,
					useCORS: true,
					allowTaint: true,
					backgroundColor: '#ffffff'
				})
				
				screenshotOverlayMessage.value = 'Uploading screenshot...' + bulkSuffix
				
				canvas.toBlob(async (blob) => {
					try {
						const csrfToken = getCsrfToken()
						const formData = new FormData()
						formData.append('image', new File([blob], lot + '.jpg', { type: 'image/jpeg' }))
						formData.append('title', lot)
						
						const response = await fetch('/api/site-plan/upload-image', {
							method: 'POST',
							headers: { 'X-CSRF-Token': csrfToken },
							body: formData
						})
						
						const data = await response.json()
						
						if (data.success) {
							screenshotStatus.value = 'success'
							screenshotOverlayMessage.value = 'Screenshot saved for Lot ' + lot + bulkSuffix
							if (remainingList.length > 0) {
								const nextLot = remainingList[0]
								const newRemaining = remainingList.slice(1).join(',')
								const params = new URLSearchParams({ lot: nextLot, screenshot: 'true', total: String(total) })
								if (newRemaining) params.set('remaining', newRemaining)
								setTimeout(() => { window.location.href = '/map?' + params.toString() }, 2000)
							} else if (total > 1) {
								screenshotOverlayMessage.value = 'All complete'
								setTimeout(() => { window.location.href = '/map' }, 2000)
							} else {
								setTimeout(() => window.close(), 2000)
							}
						} else {
							screenshotStatus.value = 'error'
							screenshotOverlayMessage.value = 'Failed: ' + (data.message || 'Unknown error')
						}
					} catch (error) {
						screenshotOverlayMessage.value = 'Upload error: ' + error.message
					}
				}, 'image/jpeg', 0.9)
				
			} catch (error) {
				screenshotOverlayMessage.value = 'Capture error: ' + error.message
			}
		}
		
		// Helper function to get CSRF token from meta tag
		const getCsrfToken = () => {
			const metaToken = document.querySelector('meta[name="csrf-token"]')
			if (metaToken) {
				return metaToken.getAttribute('content')
			}
			// Fallback to window.Craft if available
			if (typeof window.Craft !== 'undefined' && window.Craft.csrfTokenValue) {
				return window.Craft.csrfTokenValue
			}
			throw new Error('CSRF token not found in meta tag')
		}
		
		// Cookie helper functions
		const setCookie = (name, value, days = 30) => {
			const expires = new Date()
			expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000))
			document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`
		}
		
		const getCookie = (name) => {
			const nameEQ = name + '='
			const ca = document.cookie.split(';')
			for (let i = 0; i < ca.length; i++) {
				let c = ca[i]
				while (c.charAt(0) === ' ') c = c.substring(1, c.length)
				if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
			}
			return null
		}
		
		const formatPostDateText = (postDate) => {
			if (!postDate) return ''
			const d = new Date(postDate)
			return isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
		}
		
		// Handle stage ID update - save to cookie
		const handleStageIdUpdate = (newStageId) => {
			stageId.value = newStageId
			// Save to cookie for future use
			if (newStageId) {
				setCookie('site-plan-last-stage', newStageId)
			}
		}
		
		// Create new entry
		const createEntry = async () => {
			if (!selectedLot.value) return
			
			isCreatingEntry.value = true
			
			// Store the lot title to find it after refetch
			const lotTitle = selectedLot.value.title
			
			try {
				// Get CSRF token from meta tag
				const csrfToken = getCsrfToken()
				
				const requestBody = {
					title: selectedLot.value.title,
					sectionSize: sectionSize.value ? String(sectionSize.value).trim() || null : null,
					price: price.value ? parseFloat(price.value) : null,
					stageId: stageId.value ? parseInt(stageId.value) : null,
					sectionStatus: status.value || null,
					sectionType: sectionType.value || null,
					enabled: enabled.value,
					postDate: postDate.value || null,
					lotId: selectedLot.value.id
				}
				// console.log('Creating entry with sectionType:', sectionType.value, 'Full body:', requestBody)
				
				const response = await fetch('/api/site-plan/create-entry', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrfToken
					},
					body: JSON.stringify(requestBody)
				})
				
				const data = await response.json()
				
				// console.log('Create entry response:', data)
				// console.log('Response status:', response.status)
				
				if (data.success && data.entryId) {
					// console.log('Entry created successfully:', data)
					
					// Save selected stage to cookie for next entry
					if (stageId.value) {
						setCookie('site-plan-last-stage', stageId.value)
					}
					
					// Refetch sections data from Apollo (force network to get fresh data from DB)
					await loadSectionsData(true)
					
					// Find the newly created entry in the refetched data
					const newLot = sectionsData?.data?.find(s => s.title === lotTitle)
					
					if (newLot) {
						// Clear form fields but keep stageId (it's saved in cookie) and set default status
						sectionSize.value = ''
						price.value = ''
						status.value = 'available'
						sectionType.value = ''
						
						// Update selectedLot with fresh data from database
						selectedLot.value = newLot
						
						// Update text elements to reflect changes
						updateTextElements()
						
						console.log('New lot data:', newLot)
					} else {
						console.warn('Could not find new lot in refetched data')
						// Still clear form fields but keep stageId and set default status
						sectionSize.value = ''
						price.value = ''
						status.value = 'available'
						sectionType.value = ''
					}
				} else {
					console.error('Failed to create entry:', data.error)
					alert('Failed to create entry: ' + (data.error || 'Unknown error'))
				}
			} catch (error) {
				console.error('Error creating entry:', error)
				alert('Error creating entry: ' + error.message)
			} finally {
				isCreatingEntry.value = false
			}
		}

		// Start edit mode - pre-fill form with existing data
		const startEdit = (event) => {
			if (event) {
				event.stopPropagation()
			}
			
			if (!selectedLot.value || !isAdmin.value) return
			
			isEditMode.value = true
			
			// Pre-fill form fields from selectedLot
			// Stage is an array, so get the first stage's ID
			const lotStage = Array.isArray(selectedLot.value.stage) && selectedLot.value.stage.length > 0
				? selectedLot.value.stage[0]
				: selectedLot.value.stage
			const lotStageId = lotStage?.id
			
			// Set stageId - use existing value, or cookie, or first available stage
			if (lotStageId) {
				stageId.value = String(lotStageId)
			} else {
				const savedStageId = getCookie('site-plan-last-stage')
				if (savedStageId && availableStages.value.some(s => String(s.id) === savedStageId)) {
					stageId.value = savedStageId
				} else if (availableStages.value.length > 0) {
					// Use first available stage if no existing value and no cookie
					stageId.value = String(availableStages.value[0].id)
				} else {
					stageId.value = ''
				}
			}
			
			// Set status - use existing value or default to "available"
			status.value = selectedLot.value.status || 'available'
			
			sectionSize.value = selectedLot.value.sectionSize || ''
			price.value = selectedLot.value.price || ''
			enabled.value = selectedLot.value.enabled !== false
			postDate.value = selectedLot.value.postDate ? formatPostDateForInput(selectedLot.value.postDate) : '' 
			
			// Handle sectionType - it might be an object with value/label or a string
			// Default to "section" if no value provided
			if (selectedLot.value.sectionType) {
				if (typeof selectedLot.value.sectionType === 'object' && selectedLot.value.sectionType.value) {
					sectionType.value = selectedLot.value.sectionType.value
				} else {
					sectionType.value = selectedLot.value.sectionType
				}
			} else {
				sectionType.value = 'section'
			}
			
			console.log('Edit mode started', {
				sectionType: sectionType.value,
				selectedLotSectionType: selectedLot.value.sectionType,
				isEditMode: isEditMode.value,
				selectedLot: selectedLot.value,
				selectedLotStage: selectedLot.value.stage,
				selectedLotSectionSize: selectedLot.value.sectionSize,
				selectedLotPrice: selectedLot.value.price,
				stageId: stageId.value,
				sectionSize: sectionSize.value,
				price: price.value,
				lotStage: selectedLot.value.stage,
				lotStageId: lotStageId,
				postDate: postDate.value
			})
		}

		// Cancel edit mode - reset form and hide it
		const cancelEdit = () => {
			isEditMode.value = false
			// Clear form fields
			stageId.value = ''
			status.value = ''
			sectionSize.value = ''
			price.value = ''
			sectionType.value = ''
			enabled.value = true
			postDate.value = ''
		}

		// Update existing entry
		const updateEntry = async () => {
			if (!selectedLot.value || !selectedLot.value.id) return
			
			isUpdatingEntry.value = true
			
			// Store the lot ID and title to find it after refetch
			const lotId = selectedLot.value.id
			const lotTitle = selectedLot.value.title
			
			try {
				// Get CSRF token from meta tag
				const csrfToken = getCsrfToken()
				
				const requestBody = {
					entryId: selectedLot.value.id,
					sectionSize: sectionSize.value ? String(sectionSize.value).trim() || null : null,
					price: price.value ? parseFloat(price.value) : null,
					stageId: stageId.value ? parseInt(stageId.value) : null,
					sectionStatus: status.value || null,
					sectionType: sectionType.value || null,
					postDate: postDate.value || null,
					enabled: enabled.value
				}
				console.log('Updating entry with sectionType:', sectionType.value, 'Full body:', requestBody)
				
				const response = await fetch('/api/site-plan/update-entry', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrfToken
					},
					body: JSON.stringify(requestBody)
				})
				
				const data = await response.json()
				
				console.log('Update entry response:', data)
				console.log('Response status:', response.status)
				
				if (data.success) {
					console.log('Entry updated successfully:', data)
					
					// Refetch sections data from Apollo (force network to get fresh data from DB)
					await loadSectionsData(true)
					
					// Find the updated entry in the refetched data (try by ID first, then by title)
					const updatedLot = sectionsData?.data?.find(s => String(s.id) === String(lotId)) || 
									 sectionsData?.data?.find(s => s.title === lotTitle)
					
					if (updatedLot) {
						// Close edit mode
						isEditMode.value = false
						
						// Clear form fields (they'll be repopulated when Edit is clicked again)
						stageId.value = ''
						status.value = ''
						sectionSize.value = ''
						price.value = ''
						sectionType.value = ''
						postDate.value = ''
						
						// Update selectedLot with fresh data from database
						// Create a new object to ensure Vue reactivity picks up the changes
						selectedLot.value = { ...updatedLot }
						
						// Update text elements to reflect changes
						updateTextElements()
						
						// console.log('Updated lot data:', updatedLot)
						// console.log('selectedLot.value after update:', selectedLot.value)
					} else {
						console.warn('Could not find updated lot in refetched data', {
							lotId,
							lotTitle,
							availableIds: sectionsData?.data?.map(s => s.id),
							availableTitles: sectionsData?.data?.map(s => s.title)
						})
						// Still close edit mode even if we can't find the lot
						isEditMode.value = false
						stageId.value = ''
						status.value = ''
						sectionSize.value = ''
						price.value = ''
						sectionType.value = ''
						postDate.value = ''
					}
				} else {
					console.error('Failed to update entry:', data.error)
					alert('Failed to update entry: ' + (data.error || 'Unknown error'))
				}
			} catch (error) {
				console.error('Error updating entry:', error)
				alert('Error updating entry: ' + error.message)
			} finally {
				isUpdatingEntry.value = false
			}
		}
		
		// Initialize SVG with pan/zoom
		const initializeMap = async () => {
		  if (!mapContent.value) return

			// Clean up previous draw instance and handlers before re-init
			if (draw) {
				cleanupLotHandlers()
				draw.off()  // remove all SVG.js event listeners
				draw.remove()
				draw = null
				isPinchZoomActive = false
			}

		  handlersInitialized = false
		  
		  try {
			// Clear any existing content
			mapContent.value.innerHTML = ''
			
			// Create SVG container (using same dimensions as map.js)
			const response = await fetch(currentMapSvgUrl.value)
			if (!response.ok) {
				throw new Error(`Failed to load map SVG: ${response.status} ${response.statusText}`)
			}
			const svgText = await response.text()
			const parser = new DOMParser()
			const svgDoc = parser.parseFromString(svgText, 'image/svg+xml')
			const svgElement = svgDoc.querySelector('svg')

			// Read dimensions dynamically from the SVG
			let svgW = 5311.66
			let svgH = 3697.03

			if (svgElement) {
				const viewBox = svgElement.getAttribute('viewBox')
				if (viewBox) {
					const parts = viewBox.split(/[\s,]+/)
					if (parts.length === 4) {
						svgW = parseFloat(parts[2])
						svgH = parseFloat(parts[3])
						console.log('SVG W: ', svgW);
						console.log('SVG H:', svgH);
					}
				} else {
					// Fall back to width/height attributes
					const w = parseFloat(svgElement.getAttribute('width'))
					const h = parseFloat(svgElement.getAttribute('height'))
					if (!isNaN(w) && !isNaN(h)) {
						svgW = w
						svgH = h
					}
				}
			}

			const defaultViewbox = '0 0 ' + svgW + ' ' + svgH


			const zoomFactor = 0.2
			const zoomMin = 0.15
			const zoomMax = 10
			
			draw = SVG()
			  	.addTo(mapContent.value)
			  	.size('100%', '100%')
				.viewbox(defaultViewbox)
				.panZoom({ 
					zoomFactor: zoomFactor,
					zoomMin: zoomMin, 
					zoomMax: zoomMax,
    				oneFingerPan: !(isEmbedded.value && isMobile.value),
					wheelZoom: true,
				})
			
			if (svgElement) {
			  // Extract only the inner content, not the SVG wrapper
			  const innerHTML = svgElement.innerHTML
			  draw.node.innerHTML = innerHTML
			  // Hide labels immediately to prevent them flashing before the
			  // 500ms initialisation timeout runs (WES-68)
			  updateDimensionsVisibility(null)
			  updateAmenitiesVisibility()
			}

			// Initial focus: center on the first stage layer in the raw SVG,
			// before we have any stage availability data.
			const initialStageElements = draw.find('[id^="stage_"]')
			if (initialStageElements.length > 0) {
			  const firstStageElement = initialStageElements[0]
			  const bbox = firstStageElement.bbox()
			  
			  // Add padding around the stage
			  const padding = 200
			  const width = bbox.width + (padding * 2)
			  const height = bbox.height + (padding * 2)
			  const centerX = bbox.x - padding + (width / 2)
			  const centerY = bbox.y - padding + (height / 2)
			  
			  // Calculate the viewBox to fit the stage
			  const currentViewBox = draw.viewbox()
			  const scaleX = currentViewBox.width / width
			  const scaleY = currentViewBox.height / height
			  const scale = Math.min(scaleX, scaleY) * (isMobile.value ? 2.0 : 0.7)
			  
			  const newWidth = currentViewBox.width / scale
			  const newHeight = currentViewBox.height / scale
			  const newX = centerX - (newWidth / 2)
			  const newY = centerY - (newHeight / 2)
			  
			  // Set the initial viewBox immediately (no animation) so the map
			  // opens roughly centered on the first stage layer.
			  draw.viewbox(newX, newY, newWidth, newHeight)
			  
			  console.log('Initial focus on first stage layer')
			}
			
			// Set cursor styles
			mapWrapper.value.style.cursor = 'grab'
			
			// Handle mouse events for cursor changes and drag tracking
			draw.on('mousedown', () => {
			  mapWrapper.value.style.cursor = 'grabbing'
			  isMouseDown.value = true
			  draw.attr({'data-dragging':'true'})
			})
			
			draw.on('mouseup', () => {
			  mapWrapper.value.style.cursor = 'grab'
			  isMouseDown.value = false
			  draw.attr({'data-dragging':'false'})
			  setTimeout(() => {
				isDragging.value = false
			  }, 100)
			})
			
			draw.on('mouseleave', () => {
			  mapWrapper.value.style.cursor = 'grab'
			})
			
			draw.on('mousemove', () => {
			  if (isMouseDown.value) {
				isDragging.value = true
				draw.attr({'data-dragging':'true'})
				lastDragAt = Date.now()
			  }
			})
			
			// Keep viewBox inside full map bounds (svgW x svgH); center when zoomed out past edges
			const viewBoxBoundary = () => {
				if (!draw) return
				const vb = draw.viewbox()
				const x = vb.x
				const y = vb.y
				const w = vb.width != null ? vb.width : vb.w
				const h = vb.height != null ? vb.height : vb.h
				if (w == null || h == null || !Number.isFinite(w) || !Number.isFinite(h)) return

				let nx = x
				let ny = y
				if (w >= svgW) {
					nx = (svgW - w) / 2
				} else {
					nx = Math.max(0, Math.min(x, svgW - w))
				}
				if (h >= svgH) {
					ny = (svgH - h) / 2
				} else {
					ny = Math.max(0, Math.min(y, svgH - h))
				}
				if (nx !== x || ny !== y) {
					draw.viewbox(nx, ny, w, h)
				}
			}

			const scheduleViewBoxBoundaryOnZoom = () => {
				if (isPinchZoomActive) return
				if (viewBoxBoundaryZoomRaf != null) return
				viewBoxBoundaryZoomRaf = requestAnimationFrame(() => {
					viewBoxBoundaryZoomRaf = null
					if (isPinchZoomActive) return
					viewBoxBoundary()
				})
			}

			viewBoxBoundary()

			draw.draggable()

			draw.on('dragmove', e => {
				isDragging.value = true
				draw.attr({'data-dragging':'true'})
				lastDragAt = Date.now()
				viewBoxBoundary()
			})

			draw.on('dragend', e => {
				setTimeout(() => {
					isDragging.value = false
					draw.attr({'data-dragging':'false'})
				}, 100)
			})

			
			draw.on('panEnd', viewBoxBoundary)
			draw.on('zoom', scheduleViewBoxBoundaryOnZoom)
			draw.on('pinchZoomStart', () => {
				isPinchZoomActive = true
				if (viewBoxBoundaryZoomRaf != null) {
					cancelAnimationFrame(viewBoxBoundaryZoomRaf)
					viewBoxBoundaryZoomRaf = null
				}
			})
			draw.on('pinchZoomEnd', () => {
				isPinchZoomActive = false
				viewBoxBoundary()
			})
			
			// Load sections data and update text elements
			await loadSectionsData()
			
			// Add click functionality for lots elements
			setTimeout(async () => {
			  updateDimensionsVisibility(null)
			  updateAmenitiesVisibility()
			  addLotsClickHandlers()
			  updateTextElements()
			  await loadAvailableStages()
			  
			  // Check for lot from data attribute or URL and auto-select
			  const lotToSelect = getLotFromMountElement() || getLotFromURL()
			  if (lotToSelect) {
				await selectLotByNumber(lotToSelect)
			  } else {
				// Check for stage parameter in URL and auto-zoom
				const stageFromURL = getStageFromURL()
				if (stageFromURL) {
				  await zoomToStage(stageFromURL)
				} else {
				  // Only focus on stages if no lot or stage parameter exists
				  await focusOnStages()
				}
			  }

   			  checkScreenshotMode()

			  if (showDownloadOverlay.value && downloadStageFromUrl.value && downloadTypeFromUrl.value) {
				const stageTitle = downloadStageFromUrl.value
				const type = downloadTypeFromUrl.value
				const stageValid = availableStages.value.some(s => s.title === stageTitle)
				if (stageValid) {
				  if (type === 'map') {
					downloadMap(stageTitle)
				  } else {
					beginPriceListDownload(stageTitle)
				  }
				}
			  }
			}, 500) // Wait for SVG content to be fully loaded
			
		  } catch (error) {
			console.error('Error loading SVG:', error)
		  }
		}
		
		// Transform Apollo entries to sections data format (shared for initial load and cache-and-network update)
		const transformEntriesToSectionsData = (entries) => {
		  if (!entries || !Array.isArray(entries)) return { data: [] }
		  const transformedEntries = entries.map(entry => {
			const transformed = { ...entry }
			// Preserve enabled flag (drives Enable/Disable toggle)
			transformed.enabled = entry.enabled !== false
			// Craft entry status (pending, enabled, disabled); keep before overwriting status with sectionStatus
			transformed.entryStatus = entry.status ?? null
			transformed.postDate = entry.postDate ?? null
			if (entry.sectionStatus) {
			  if (typeof entry.sectionStatus === 'string') {
				transformed.status = entry.sectionStatus
				transformed.statusLabel = entry.sectionStatus === 'underOffer' ? 'Under offer' : entry.sectionStatus === 'sold' ? 'Sold' : entry.sectionStatus === 'available' ? 'Available' : entry.sectionStatus
			  } else if (entry.sectionStatus.value) {
				transformed.status = entry.sectionStatus.value
				transformed.statusLabel = entry.sectionStatus.label || (entry.sectionStatus.value === 'underOffer' ? 'Under offer' : entry.sectionStatus.value === 'sold' ? 'Sold' : entry.sectionStatus.value === 'available' ? 'Available' : entry.sectionStatus.value)
			  }
			  delete transformed.sectionStatus
			}
			if (entry.sectionType) {
			  if (typeof entry.sectionType === 'string') {
				transformed.sectionType = entry.sectionType
				transformed.sectionTypeLabel = entry.sectionType === 'section' ? 'Section' : entry.sectionType === 'homeLand' ? 'Home & land' : entry.sectionType
			  } else if (entry.sectionType.value) {
				transformed.sectionType = entry.sectionType.value
				transformed.sectionTypeLabel = entry.sectionType.label || (entry.sectionType.value === 'section' ? 'Section' : entry.sectionType.value === 'homeLand' ? 'Home & land' : entry.sectionType.value)
			  }
			}
			return transformed
		  })
		  return { data: transformedEntries }
		}

		// Load sections data using Apollo GraphQL (cache-and-network: show cache first, then update when fresh data arrives)
		const loadSectionsData = async (forceNetwork = false) => {
		  try {
			// Query returns all (enabled + disabled + pending); for non-admin, show only live enabled (pending/disabled appear like unlinked)
			const applyFilter = (data) => {
			  if (!isAdmin.value) {
				data.data = data.data.filter(s => s.enabled !== false && s.entryStatus !== 'pending')
			  }
			  return data
			}
			const onUpdate = (result) => {
			  if (result?.data?.entries) {
				sectionsData = applyFilter(transformEntriesToSectionsData(result.data.entries))
				sectionsEntriesRef.value = sectionsData.data || []
				updateTextElements()
				console.log('Loaded sections data (background refresh):', sectionsData)
			  }
			}
			const result = await executeQueryWithCacheAndNetwork(apolloClient, GET_ALL_SECTIONS, {}, forceNetwork, forceNetwork ? null : onUpdate)
			
			if (result && result.data && result.data.entries) {
			  sectionsData = applyFilter(transformEntriesToSectionsData(result.data.entries))
			  sectionsEntriesRef.value = sectionsData.data || []
			  console.log('Loaded sections data:', sectionsData)
			  return sectionsData
			}
			return null
		  } catch (error) {
			console.error('Error loading sections data:', error)
			return null
		  }
		}

		// Load current map SVG URL from Maps section
		const loadCurrentMap = async () => {
			try {
				// bypass apollo cache here for switching
				const result = await executeQueryWithCacheAndNetwork(apolloClient, GET_CURRENT_MAP, {}, true)
				
				if (result?.data?.entries && result.data.entries.length > 0) {
				const mapEntry = result.data.entries[0]
				activeMapId.value = mapEntry.id  // <── ADD THIS LINE
				if (mapEntry.svgFile && mapEntry.svgFile.length > 0) {
					currentMapSvgUrl.value = mapEntry.svgFile[0].url
					console.log('Loaded map SVG from CMS:', currentMapSvgUrl.value)
				} else {
					console.warn('No SVG file found in map entry, using fallback')
				}
				} else {
				console.warn('No map entries found, using fallback SVG')
				}
			} catch (error) {
				console.error('Error loading map SVG:', error)
			}
		}
		
		// Update lot shapes with section status data attributes
		const updateTextElements = () => {
		  if (!draw || !sectionsData) return
		  
		  // Find the lots group
		  const lotsGroup = draw.findOne('#lots')
		  if (!lotsGroup) return
		  
		  // Find all lot elements with IDs starting with "_" followed by numbers
		  const lotsElements = lotsGroup.find('[id^="_"]')
		  
		  lotsElements.forEach(element => {
			const id = element.attr('id')
			if (id && /^_\d+(-\d+)?$/.test(id)) {
			  const lotNumber = getLotNumberFromId(id)
			  
			  // Find matching section data
			  const section = sectionsData.data.find(s => s.title === lotNumber)
			  
			  if (section) {
				// Add data attributes directly to the lot shape
				element.attr('data-section-id', section.id)
				element.attr('data-section-type', section.sectionType || null)
				element.attr('data-section-title', section.title)
				element.attr('data-section-status', isScreenshotMode.value ? 'available' : section.status)

				// Townhouse flag
				element.attr(
					'data-section-townhouse',
					section.__typename === 'townhouse_Entry' ? 'true' : null
				)

				// In screenshot mode: present disabled/pending lots as enabled for capture
				if (isScreenshotMode.value) {
				  element.attr('data-section-enabled', 'true')
				  element.attr('data-section-pending', null)
				} else {
				  element.attr('data-section-enabled', section.enabled !== false ? 'true' : 'false')
				  element.attr('data-section-pending', isAdmin.value && section.entryStatus === 'pending' ? 'true' : null)
				}
				
				// console.log(`Updated lot ${lotNumber} (ID: ${section.id}) with status: ${section.status}, type: ${section.sectionType}, enabled: ${section.enabled}`)
			  } else {
				// Clear data attributes if no section found; still set data-section-enabled for every lot
				element.attr('data-section-id', null)
				element.attr('data-section-type', null)
				element.attr('data-section-title', null)
				element.attr('data-section-status', null)
				element.attr('data-section-enabled', 'true')
				element.attr('data-section-pending', null)
			  }
			}
		  })
		}

		// Compute lot numbers for SVG lot shapes without a linked CMS entry,
		// using sectionsData (GraphQL) as the source of truth.
		const getUnlinkedLots = async () => {
		  const result = []
		  if (!draw) return result

		  console.log('[Bulk] getUnlinkedLots: starting. sectionsData exists?', !!sectionsData)

		  // Ensure we have the latest sections data
		  if (!sectionsData) {
			console.log('[Bulk] getUnlinkedLots: sectionsData missing, calling loadSectionsData()')
			await loadSectionsData()
		  }

		  console.log('[Bulk] getUnlinkedLots: sectionsData after load:', {
			hasSectionsData: !!sectionsData,
			entriesCount: sectionsData?.data?.length
		  })

		  if (!sectionsData || !sectionsData.data) {
			console.log('[Bulk] getUnlinkedLots: no sectionsData available, returning empty list')
			return result
		  }

		  const lotsGroup = draw.findOne('#lots')
		  if (!lotsGroup) {
			console.log('[Bulk] getUnlinkedLots: #lots group not found in SVG')
			return result
		  }

		  const lotsElements = lotsGroup.find('[id^=\"_\"]')
		  console.log('[Bulk] getUnlinkedLots: lots elements found:', lotsElements.length)

		  lotsElements.forEach(element => {
      const id = element.attr('id')
      if (id && /^_\d+(-\d+)?$/.test(id)) {
			  const lotNumber = getLotNumberFromId(id)
			  if (lotNumber && !sectionsData.data.some(s => s.title === lotNumber) && !result.includes(lotNumber)) {
				result.push(lotNumber)
			  }
			}
		  })

		  console.log('[Bulk] getUnlinkedLots: final unlinked lots:', result)
		  return result
		}
		
		
		// Lot click handler function
		const handleLotClick = (e, element, lotNumber) => {
		  e.stopPropagation()
		  
		  const lotData = sectionsData?.data.find(s => s.title === lotNumber)
		  // Non-admin: disabled lots are not clickable
		  if (lotData && lotData.enabled === false && !isAdmin.value) {
			return
		  }
				
		  // Check if lot is already selected
		  const isAlreadySelected = element.hasClass('lot-selected')
				
		  // Toggle CSS class
		  if (isAlreadySelected) {
			element.removeClass('lot-selected')
			// Hide popup if lot was deselected
			closePopup()
			// Remove lot parameter from URL
			updateURLWithLot(null)
			updateDimensionsVisibility(null)
		  } else {
			// First, deselect any other selected lots
			resetLotSelections()
			closePopup()
			
			// Select this lot
			element.addClass('lot-selected')
			updateDimensionsVisibility(lotNumber)
			
			// Update URL with lot parameter
			updateURLWithLot(lotNumber)
			
			// Find lot data and show popup only when selecting
			console.log('handleLotClick - lot data lookup:', {
			  lotNumber: lotNumber,
			  sectionsDataExists: !!sectionsData,
			  sectionsDataLength: sectionsData?.data?.length,
			  lotDataFound: !!lotData,
			  isAdmin: isAdmin.value
			})
			if (lotData) {
			  console.log('Showing popup with lot data:', lotData)
			  showLotPopup(lotData, e)
			} else if (isAdmin.value) {
			  // For admin users, show popup even if no entry data exists
			  const adminLotData = {
				id: lotNumber,
				title: lotNumber,
				price: 0,
				status: 'no-entry',
				statusLabel: '',
				url: '#'
			  }
			  console.log('Showing popup with admin lot data:', adminLotData)
			  showLotPopup(adminLotData, e)
			} else {
			  console.log('No popup shown - lot data not found and user is not admin')
			}
			// Zoom to the lot
			zoomToLot(element)
		  }
				
		  // Bring element to front
		  if (element.hasClass('lot-selected')) {
				element.front()
			}
				
		  console.log('Clicked lot element:', element.attr('id'), 'Selected:', element.hasClass('lot-selected'))
		}

		const cleanupLotHandlers = () => {
			if (!draw) return
			const lotsGroup = draw.findOne('#lots')
			if (!lotsGroup) return
			const lotsElements = lotsGroup.find('[id^="_"]')
			lotsElements.forEach(element => {
				if (element._touchStartHandler) {
					element.node.removeEventListener('touchstart', element._touchStartHandler)
					delete element._touchStartHandler
				}
				if (element._touchEndHandler) {
					element.node.removeEventListener('touchend', element._touchEndHandler)
					delete element._touchEndHandler
				}
				element.off('mousedown')
				element.off('click')
			})
		}
		
		// Add click handlers for lots elements
		const addLotsClickHandlers = () => {
			if (!draw) return

			if (handlersInitialized) {
				console.warn('[Handlers] Already initialized, skipping')
				return
			}
			
			const lotsGroup = draw.findOne('#lots')
			if (!lotsGroup) return
			
			const lotsElements = lotsGroup.find('[id^="_"]')
			
			lotsElements.forEach(element => {
				const id = element.attr('id')
				if (id && /^_\d+(-\d+)?$/.test(id)) {
				
				if (isMobile.value) {
					let touchStartPos = null
					let touchStartTime = null
					const moveThreshold = 10
					
					if (element._touchStartHandler) {
					element.node.removeEventListener('touchstart', element._touchStartHandler)
					}
					if (element._touchEndHandler) {
					element.node.removeEventListener('touchend', element._touchEndHandler)
					}
					
					element._touchStartHandler = function(e) {
					// Only single-finger touches
					if (e.touches.length > 1) return
					
					// Clear overlay timer when touching a lot
					if (scrollOverlayTimer.value) {
						clearTimeout(scrollOverlayTimer.value)
						scrollOverlayTimer.value = null
					}
					showScrollOverlay.value = false
					
					const touch = e.touches[0]
					touchStartPos = { x: touch.clientX, y: touch.clientY }
					touchStartTime = Date.now()
					}
					
					element._touchEndHandler = function(e) {
					if (!touchStartPos || !touchStartTime) return
					if (e.touches.length > 0) return // Multi-touch, ignore
					
					const touch = e.changedTouches[0]
					const dx = touch.clientX - touchStartPos.x
					const dy = touch.clientY - touchStartPos.y
					const distance = Math.sqrt(dx * dx + dy * dy)
					const duration = Date.now() - touchStartTime
					
					// Reset tracking
					const wasTap = duration < 300 && distance < moveThreshold && !isDragging.value
					touchStartPos = null
					touchStartTime = null
					
					// Reset isDragging
					setTimeout(() => {
						isDragging.value = false
					}, 50)
					
					if (wasTap) {
						const lotNumber = getLotNumberFromId(id)
						if (lotNumber) {
							console.log('[Mobile] Lot tapped:', lotNumber)
							handleLotClickMobile(element, lotNumber)
						}
					}
					}
					
					element.node.addEventListener('touchstart', element._touchStartHandler, { passive: true })
					element.node.addEventListener('touchend', element._touchEndHandler, { passive: true })
					
				} else {
					// Desktop handlers (unchanged)
					let mouseDownX = null
					let mouseDownY = null
					let mouseMoved = false
					const clickMoveThreshold = 10
					const recentDragGraceMs = 100
					
					element.on('mousedown', function(e) {
					mouseDownX = e.clientX
					mouseDownY = e.clientY
					mouseMoved = false
					
					const handleMouseMove = (moveEvent) => {
						if (mouseDownX !== null && mouseDownY !== null) {
						const dx = moveEvent.clientX - mouseDownX
						const dy = moveEvent.clientY - mouseDownY
						if (Math.sqrt(dx * dx + dy * dy) > clickMoveThreshold) {
							mouseMoved = true
						}
						}
					}
					
					const handleMouseUp = () => {
						document.removeEventListener('mousemove', handleMouseMove)
						document.removeEventListener('mouseup', handleMouseUp)
					}
					
					document.addEventListener('mousemove', handleMouseMove)
					document.addEventListener('mouseup', handleMouseUp)
					})
					
					element.on('click', function(e) {
					e.stopPropagation()
					
					const now = Date.now()
					const recentlyDragged = lastDragAt && (now - lastDragAt < recentDragGraceMs)
					
					let actualMouseMoved = false
					if (mouseDownX !== null && mouseDownY !== null) {
						const dx = e.clientX - mouseDownX
						const dy = e.clientY - mouseDownY
						if (Math.sqrt(dx * dx + dy * dy) > clickMoveThreshold) {
						actualMouseMoved = true
						}
					}
					
					if (!isDragging.value && !mouseMoved && !actualMouseMoved && !recentlyDragged) {
						const lotNumber = getLotNumberFromId(id)
						if (lotNumber) {
							handleLotClick(e, element, lotNumber)
						}
					}
					
					mouseDownX = null
					mouseDownY = null
					mouseMoved = false
					})
				}
				}
			})

			handlersInitialized = true
			console.log(`Added click handlers to ${lotsElements.length} lots elements (mobile: ${isMobile.value})`)
		}

		const handleLotClickMobile = (element, lotNumber) => {
			const lotData = sectionsData?.data.find(s => s.title === lotNumber)

			const now = Date.now()
			if (now - lastLotClickTime < lotClickDebounce) {
				console.log('[Mobile] Click debounced')
				return
			}
			lastLotClickTime = now
			
			// Non-admin: disabled lots are not clickable
			if (lotData && lotData.enabled === false && !isAdmin.value) {
				return
			}
			
			// Check if lot is already selected
			const isAlreadySelected = element.hasClass('lot-selected')
			
			if (isAlreadySelected) {
				element.removeClass('lot-selected')
				closePopup()
				updateURLWithLot(null)
				updateDimensionsVisibility(null)
			} else {
				// Deselect other lots
				resetLotSelections()
				closePopup()
				
				// Select this lot
				element.addClass('lot-selected')
				updateDimensionsVisibility(lotNumber)
				updateURLWithLot(lotNumber)
				
				// Show popup
				if (lotData) {
				console.log('[Mobile] Showing popup with lot data:', lotData)
				showLotPopup(lotData, null)
				} else if (isAdmin.value) {
				const adminLotData = {
					id: lotNumber,
					title: lotNumber,
					price: 0,
					status: 'no-entry',
					statusLabel: '',
					url: '#'
				}
				console.log('[Mobile] Showing popup with admin lot data:', adminLotData)
				showLotPopup(adminLotData, null)
				}
				
				// Zoom to lot
				zoomToLot(element)
			}
			
			// Bring to front
			if (element.hasClass('lot-selected')) {
				element.front()
			}

			
			console.log('[Mobile] Lot selected:', element.attr('id'), 'Selected:', element.hasClass('lot-selected'))
		}
		
		// Reset all lot selections
		const resetLotSelections = () => {
			if (!draw) return
			
			updateDimensionsVisibility(null)
			
			const lotsGroup = draw.findOne('#lots')
			if (!lotsGroup) return
			
			// Only proceed if there are actually selected lots
			const selectedLots = lotsGroup.find('[id^="_"].lot-selected')
			if (selectedLots.length === 0) return
			
			selectedLots.forEach(element => {
				const id = element.attr('id')
				if (id && /^_\d+(-\d+)?$/.test(id)) {
				element.removeClass('lot-selected')
				}
			})
			
			console.log('Reset all lot selections')
		}
		
		// Zoom to a specific lot element
		const zoomToLot = (element) => {
			if (!draw) return
			
			// Cancel any running animation to prevent queue buildup
			try { draw.timeline().stop() } catch(e) { /* no active timeline */ }
			
			// Get the bounding box of the lot element
			const bbox = element.bbox()
		  
			// Add padding around the lot
			const padding = 50
			const width = bbox.width + (padding * 2)
			const height = bbox.height + (padding * 2)
			const centerX = bbox.x - padding + (width / 2)
			const centerY = bbox.y - padding + (height / 2)
			
			// Calculate the viewBox to fit the lot
			const currentViewBox = draw.viewbox()
			const scaleX = currentViewBox.width / width
			const scaleY = currentViewBox.height / height
			const scale = Math.min(scaleX, scaleY) * 0.8 // 0.8 for some margin
			
			const newWidth = currentViewBox.width / scale
			const newHeight = currentViewBox.height / scale
			const newX = centerX - (newWidth / 2)
			const newY = centerY - (newHeight / 2)
			
			// Animate the viewBox transition to the lot
			draw.animate(600, 'ease-in-out').viewbox(newX, newY, newWidth, newHeight)
			
			console.log('Zoomed to lot:', element.attr('id'))
			console.log(`Lot zoom level: ${scale.toFixed(2)}`)
		}
		
		// Load stages data using Apollo GraphQL
		const loadStagesData = async () => {
		  try {
			const result = await executeQueryWithCacheAndNetwork(apolloClient, GET_ALL_STAGES)
			
			console.log('[Stages] Raw GraphQL result:', result)
			console.log('[Stages] Result data:', result?.data)
			console.log('[Stages] Result entries:', result?.data?.entries)
			
			if (result && result.data && result.data.entries) {
			  console.log('[Stages] Found entries:', result.data.entries.length)
			  
			  // Transform Apollo result to match Element API structure
			  // GraphQL returns stageStatus as a string, but component expects status and statusLabel
			  const transformedEntries = result.data.entries.map((entry, index) => {
				const transformed = { ...entry }
				
				// Handle stageStatus - it's a string enum value from GraphQL
				if (entry.stageStatus !== null && entry.stageStatus !== undefined) {
				  // stageStatus is a string (e.g., "available")
				  transformed.status = entry.stageStatus
				  transformed.statusLabel = entry.stageStatus
				  // Remove the original stageStatus field
				  delete transformed.stageStatus
				} else {
				  // If stageStatus is missing, set a default
				  transformed.status = 'unknown'
				  transformed.statusLabel = 'Unknown'
				}
				
				return transformed
			  })
			  
			  console.log('[Stages] All transformed entries:', transformedEntries)
			  
			  return {
				data: transformedEntries
			  }
			}
			
			console.warn('[Stages] No entries found in result:', result)
			return null
		  } catch (error) {
			console.error('[Stages] Error loading stages data:', error)
			console.error('[Stages] Error stack:', error.stack)
			return null
		  }
		}
		
		// Focus viewBox on available stages elements
		const focusOnStages = async () => {
		  if (!draw) return
		  
		  // Load stages data
		  const stagesData = await loadStagesData()
		  if (!stagesData || !stagesData.data) {
			console.log('No stages data available')
			return
		  }
		  
		  // Find all elements with IDs starting with "stage_"
		  const stagesElements = draw.find('[id^="stage_"]')
		  if (stagesElements.length === 0) {
			console.log('No stage elements found')
			return
		  }
		  
		  // Filter to only available stages
		  const availableStages = []
		  stagesElements.forEach(element => {
			const id = element.attr('id')
			if (id && /^stage_\d+$/.test(id)) {
			  const stageTitle = id.replace('stage_', '')
			  const stageData = stagesData.data.find(s => s.title === stageTitle)
			  if (stageData && stageData.status === 'available') {
				availableStages.push(element)
			  }
			}
		  })
		  
		  if (availableStages.length === 0) {
			console.log('No available stages found')
			return
		  }
		  
		  // Calculate bounding box for available stage elements only
		  let minX = Infinity
		  let minY = Infinity
		  let maxX = -Infinity
		  let maxY = -Infinity
		  
		  availableStages.forEach(element => {
			const bbox = element.bbox()
			minX = Math.min(minX, bbox.x)
			minY = Math.min(minY, bbox.y)
			maxX = Math.max(maxX, bbox.x + bbox.width)
			maxY = Math.max(maxY, bbox.y + bbox.height)
		  })
		  
		  // Add minimal padding around the elements for tighter zoom
		  const padding = 20
		  const width = maxX - minX + (padding * 2)
		  const height = maxY - minY + (padding * 2)
		  const centerX = minX - padding + (width / 2)
		  const centerY = minY - padding + (height / 2)
		  
		  // Calculate the viewBox to fit the available stages with more zoom
		  const currentViewBox = draw.viewbox()
		  const scaleX = currentViewBox.width / width
		  const scaleY = currentViewBox.height / height
		  const scale = Math.min(scaleX, scaleY) * 1.2      
		  const newWidth = currentViewBox.width / scale
		  const newHeight = currentViewBox.height / scale
		  const newX = centerX - (newWidth / 2)
		  const newY = centerY - (newHeight / 2)
		  
		  // Animate the viewBox transition
		try { draw.timeline().stop() } catch(e) { /* no active timeline */ }
		draw.animate(800, 'ease-in-out').viewbox(newX, newY, newWidth, newHeight)
		  
		  console.log(`Focused on ${availableStages.length} available stage elements`)
		  console.log(`Initial zoom level: ${scale.toFixed(2)}`)
		}
		
		// Load and populate available stages for the panel
		const loadAvailableStages = async () => {
		  try {
			const stagesData = await loadStagesData()
			
			if (stagesData && stagesData.data) {
			  // Filter to only stages with status 'available'
			  const available = stagesData.data.filter(stage => stage.status === 'available')
			  availableStages.value = available
			  console.log(`Loaded ${available.length} available stages for panel`)
			}
		  } catch (error) {
			console.error('Error loading available stages:', error)
		  } finally {
			hasLoadedAvailableStages.value = true
		  }
		}
		
		// Zoom to a specific stage by title
		const zoomToStage = async (stageTitle) => {
			if (!draw) return
			
			// Cancel any running animation to prevent queue buildup
			try { draw.timeline().stop() } catch(e) { /* no active timeline */ }
			
			const stagesGroup = draw.findOne('#stages')
		  if (!stagesGroup) {
			console.log('Stages layer #stages not found')
			return
		  }
		  const stageElement = stagesGroup.findOne(`#Stage_${stageTitle}`)
		  if (!stageElement) {
			console.log(`Stage element _${stageTitle} not found`)
			return
		  }
		  
		  // Get the bounding box of the stage element
		  const bbox = stageElement.bbox()

		  // Margin as a fraction of the stage border's own size (rather than a flat unit
		  // count) — a flat padding is a wildly different proportion depending on how big
		  // the stage border is, so small stages ended up padded far more than large ones.
		  const marginFraction = 0.15
		  const paddedWidth = bbox.width * (1 + marginFraction * 2)
		  const paddedHeight = bbox.height * (1 + marginFraction * 2)
		  const centerX = bbox.x + bbox.width / 2
		  const centerY = bbox.y + bbox.height / 2

		  // Fit the padded stage bbox to the container's actual on-screen aspect ratio,
		  // not the CURRENT viewBox's extent — deriving the scale from currentViewBox made
		  // the result depend on whatever pan/zoom state (or initial default extent) was
		  // active before this call, so the same stage zoomed to wildly different levels
		  // depending on entry state/device. This way the zoom is determined purely by the
		  // stage's own size and the screen's shape, so it's consistent everywhere.
		  const containerEl = mapWrapper.value || mapContent.value
		  const currentViewBox = draw.viewbox()
		  const containerAspect = containerEl && containerEl.clientWidth && containerEl.clientHeight
			? containerEl.clientWidth / containerEl.clientHeight
			: (currentViewBox.width / currentViewBox.height)

		  let newWidth, newHeight
		  if (paddedWidth / paddedHeight > containerAspect) {
			newWidth = paddedWidth
			newHeight = newWidth / containerAspect
		  } else {
			newHeight = paddedHeight
			newWidth = newHeight * containerAspect
		  }

		  const newX = centerX - (newWidth / 2)
		  const newY = centerY - (newHeight / 2)

		  // Animate the viewBox transition to the stage
		  draw.animate(800, 'ease-in-out').viewbox(newX, newY, newWidth, newHeight)
		  
		  // Update URL with stage parameter
		  updateURLWithStage(stageTitle)
		  
		  console.log(`Zoomed to stage: ${stageTitle}`)
		}

		let stageMapHattonB64Cache = null
		const loadStageMapHattonBase64 = async () => {
			if (stageMapHattonB64Cache) return stageMapHattonB64Cache
			const res = await fetch('/dist/fonts/pphatton.ttf')
			if (!res.ok) throw new Error('Could not load pp-hatton for stage map PDF')
			const buf = await res.arrayBuffer()
			const binary = new Uint8Array(buf)
			let raw = ''
			for (let i = 0; i < binary.length; i += 8192) {
				raw += String.fromCharCode.apply(null, binary.subarray(i, i + 8192))
			}
			stageMapHattonB64Cache = btoa(raw)
			return stageMapHattonB64Cache
		}

		let stageMapInterB64Cache = null
		const loadStageMapInterBase64 = async () => {
			if (stageMapInterB64Cache) return stageMapInterB64Cache
			const res = await fetch('/dist/fonts/inter.ttf')
			if (!res.ok) throw new Error('Could not load Inter for stage map PDF')
			const buf = await res.arrayBuffer()
			const binary = new Uint8Array(buf)
			let raw = ''
			for (let i = 0; i < binary.length; i += 8192) {
				raw += String.fromCharCode.apply(null, binary.subarray(i, i + 8192))
			}
			stageMapInterB64Cache = btoa(raw)
			return stageMapInterB64Cache
		}

		const registerStageMapPdfFonts = async (pdf) => {
			const [hattonB64, interB64] = await Promise.all([
				loadStageMapHattonBase64(),
				loadStageMapInterBase64()
			])
			pdf.addFileToVFS('pphatton.ttf', hattonB64)
			pdf.addFont('pphatton.ttf', 'pp-hatton', 'normal')
			pdf.addFileToVFS('inter.ttf', interB64)
			pdf.addFont('inter.ttf', 'inter', 'normal')
			pdf.addFont('inter.ttf', 'inter', 'bold')
		}

		let stageMapFigureCache = null
		const loadStageMapFigure = async () => {
			if (stageMapFigureCache) return stageMapFigureCache
			const res = await fetch('/dist/images/icons/stagemap-figure.png')
			if (!res.ok) throw new Error('Could not load stagemap-figure.png')
			const blob = await res.blob()
			const objectUrl = URL.createObjectURL(blob)
			try {
				const img = new Image()
				await new Promise((resolve, reject) => {
					img.onload = resolve
					img.onerror = () => reject(new Error('Failed to decode stagemap-figure.png'))
					img.src = objectUrl
				})
				const w = img.naturalWidth || 1
				const h = img.naturalHeight || 1
				const c = document.createElement('canvas')
				c.width = w
				c.height = h
				c.getContext('2d').drawImage(img, 0, 0)
				const dataUrl = c.toDataURL('image/png')
				stageMapFigureCache = { dataUrl, naturalWidth: w, naturalHeight: h }
				return stageMapFigureCache
			} finally {
				URL.revokeObjectURL(objectUrl)
			}
		}

		const hexToRgb = (hex) => {
			if (!hex) return [0, 0, 0]
			const cleaned = hex.replace('#', '')
			const normalized = cleaned.length === 3 ? cleaned.split('').map((c) => c + c).join('') : cleaned
			const num = parseInt(normalized, 16)
			return [(num >> 16) & 255, (num >> 8) & 255, num & 255]
		}

		const clipCroppedMapToRoundRectDataUrl = (sourceCanvas, mapWpt, mapHpt, cornerRpt, pxScale = 2) => {
			const outW = Math.max(1, Math.round(mapWpt * pxScale))
			const outH = Math.max(1, Math.round(mapHpt * pxScale))
			const r = Math.min(
				cornerRpt * pxScale,
				outW / 2 - 0.01,
				outH / 2 - 0.01
			)
			const out = document.createElement('canvas')
			out.width = outW
			out.height = outH
			const octx = out.getContext('2d')
			octx.imageSmoothingEnabled = true
			octx.imageSmoothingQuality = 'high'
			// JPEG has no alpha; unwritten pixels become black. Fill corners with page white first.
			octx.fillStyle = '#ffffff'
			octx.fillRect(0, 0, outW, outH)
			octx.beginPath()
			if (typeof octx.roundRect === 'function') {
				octx.roundRect(0, 0, outW, outH, r)
			} else {
				octx.moveTo(r, 0)
				octx.lineTo(outW - r, 0)
				octx.arcTo(outW, 0, outW, r, r)
				octx.lineTo(outW, outH - r)
				octx.arcTo(outW, outH, outW - r, outH, r)
				octx.lineTo(r, outH)
				octx.arcTo(0, outH, 0, outH - r, r)
				octx.lineTo(0, r)
				octx.arcTo(0, 0, r, 0, r)
				octx.closePath()
			}
			octx.clip()
			octx.drawImage(sourceCanvas, 0, 0, outW, outH)
			return out.toDataURL('image/jpeg', 0.95)
		}

		const STAGE_MAP_FOOTER_PNG = '/dist/images/bg/stagemap-footer.png'

		const STAGE_MAP_DESIGN_W = 1198
		const STAGE_MAP_DESIGN_H = 1696
		const STAGE_MAP_MARGIN_PX = 54
		const STAGE_MAP_SIDEBAR_PX = 383
		const STAGE_MAP_SIDEBAR_MAP_GAP_PX = 72
		const STAGE_MAP_LOGO_W_PX = 75.13
		const STAGE_MAP_LOGO_BOTTOM_PX = 18
		const STAGE_MAP_TITLE_SIZE_PX = 86
		const STAGE_MAP_TITLE_TO_TITLES_PX = 24
		const STAGE_MAP_HEADER_TOP_PX = 136
		const STAGE_MAP_SIDEBAR_SECTION_GAP_PX = 100
		const STAGE_MAP_MAP_CORNER_RADIUS_PX = 20

		// Print-quality target for the embedded map image, independent of device/viewport.
		// pxScale below is derived from this (dpi / 72pt-per-inch).
		const STAGE_MAP_TARGET_DPI = 300
		// Minimum html2canvas multiplier; bumped per-capture (see downloadMap) if the
		// capped container size below isn't enough on its own to reach the target DPI.
		const STAGE_MAP_CAPTURE_SCALE = 2
		const STAGE_MAP_CAPTURE_HEADROOM = 1.15
		// Hard cap on the temporary capture container's larger CSS side. Resolution beyond
		// this is pushed into html2canvas's `scale` multiplier (which only affects the
		// final raster) rather than the DOM size itself — real mobile Safari has been
		// observed to silently produce a blank/background-only capture when html2canvas
		// has to clone+rasterize an overly large DOM subtree, so the temporary resize is
		// kept modest regardless of the DPI target.
		const STAGE_MAP_MAX_CAPTURE_SIDE_PX = 900

		let stageMapFooterImgCache = null
		const loadStageMapFooterPng = async () => {
			if (stageMapFooterImgCache) return stageMapFooterImgCache
			const res = await fetch(STAGE_MAP_FOOTER_PNG)
			if (!res.ok) throw new Error('Could not load stage map footer PNG')
			const blob = await res.blob()
			const objectUrl = URL.createObjectURL(blob)
			try {
				const img = new Image()
				await new Promise((resolve, reject) => {
					img.onload = resolve
					img.onerror = () => reject(new Error('Failed to decode stage map footer'))
					img.src = objectUrl
				})
				const w = img.naturalWidth
				const h = img.naturalHeight
				const c = document.createElement('canvas')
				c.width = w
				c.height = h
				c.getContext('2d').drawImage(img, 0, 0)
				const dataUrl = c.toDataURL('image/png')
				stageMapFooterImgCache = { dataUrl, naturalWidth: w, naturalHeight: h }
				return stageMapFooterImgCache
			} finally {
				URL.revokeObjectURL(objectUrl)
			}
		}

		let stageMapContactCache = null
		const loadStageMapContactPng = async () => {
			if (stageMapContactCache) return stageMapContactCache
			const res = await fetch('/dist/images/bg/stagemap-contact.png')
			if (!res.ok) throw new Error('Could not load stagemap-contact.png')
			const blob = await res.blob()
			const objectUrl = URL.createObjectURL(blob)
			try {
				const img = new Image()
				await new Promise((resolve, reject) => {
					img.onload = resolve
					img.onerror = () => reject(new Error('Failed to decode stagemap-contact.png'))
					img.src = objectUrl
				})
				const w = img.naturalWidth || 1
				const h = img.naturalHeight || 1
				const c = document.createElement('canvas')
				c.width = w
				c.height = h
				c.getContext('2d').drawImage(img, 0, 0)
				const dataUrl = c.toDataURL('image/png')
				stageMapContactCache = { dataUrl, naturalWidth: w, naturalHeight: h }
				return stageMapContactCache
			} finally {
				URL.revokeObjectURL(objectUrl)
			}
		}

		const drawStageMapFooter = (pdf, pageW, pageH, footerImg) => {
			if (!footerImg?.dataUrl) return
			const drawW = pageW
			const drawH = (footerImg.naturalHeight / footerImg.naturalWidth) * pageW
			const y = pageH - drawH
			pdf.addImage(footerImg.dataUrl, 'PNG', 0, y, drawW, drawH)
		}

		const STAGE_MAP_TITLE_COLOR = [39, 51, 29]
		const STAGE_MAP_TITLES_SUB_OPACITY = 0.72
		const STAGE_MAP_LEGEND_TEXT_COLOR = [41, 59, 41]
		const STAGE_MAP_LEGEND_INTER_DOT_GAP_PX = 32
		const STAGE_MAP_LEGEND_DOT_PX = 21
		const STAGE_MAP_LEGEND_LABEL_PX = 20
		const STAGE_MAP_LEGEND_CONTACT_SECTION_GAP_PX = 100
		const STAGE_MAP_CONTACT_W_PX = 261.66

		const drawStageMapPanel = (pdf, { x, y, w, h, stageTitle, titlesText, figureMark, contactMark, designScale }) => {
			const gap100 = STAGE_MAP_SIDEBAR_SECTION_GAP_PX * designScale
			const headerTop = STAGE_MAP_HEADER_TOP_PX * designScale
			const titleSizePt = STAGE_MAP_TITLE_SIZE_PX * designScale
			const inset = 4
			const textX = x + inset
			const lineX1 = x + inset
			const lineX2 = x + w - inset

			pdf.setFillColor(255, 255, 255)
			pdf.rect(x, y, w, h, 'F')

			let titleTop = headerTop
			if (figureMark) {
				const mw = STAGE_MAP_LOGO_W_PX * designScale
				const mh = mw * (figureMark.naturalHeight / figureMark.naturalWidth)
				pdf.addImage(figureMark.dataUrl, 'PNG', textX, headerTop, mw, mh)
				titleTop = headerTop + mh + STAGE_MAP_LOGO_BOTTOM_PX * designScale
			} else {
				titleTop = headerTop + 6 * designScale
			}

			pdf.setFont('pp-hatton', 'normal')
			pdf.setFontSize(titleSizePt)
			pdf.setTextColor(...STAGE_MAP_TITLE_COLOR)
			pdf.text(`Stage ${stageTitle || ''}`, textX, titleTop, { align: 'left', baseline: 'top' })

			const titlesSubSize = 10
			const titleLineBox = titleSizePt
			const titlesTop = titleTop + titleLineBox + STAGE_MAP_TITLE_TO_TITLES_PX * designScale
			pdf.setFont('helvetica', 'normal')
			pdf.setFontSize(titlesSubSize)
			const tSub = STAGE_MAP_TITLES_SUB_OPACITY
			const tBg = 1 - tSub
			pdf.setTextColor(
				Math.round(STAGE_MAP_TITLE_COLOR[0] * tSub + 255 * tBg),
				Math.round(STAGE_MAP_TITLE_COLOR[1] * tSub + 255 * tBg),
				Math.round(STAGE_MAP_TITLE_COLOR[2] * tSub + 255 * tBg)
			)
			pdf.text(`UPDATED ${new Date().toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' })}`, textX, titlesTop, { align: 'left', baseline: 'top' })

			const subLineH = titlesSubSize * 1.05
			const sepY = titlesTop + subLineH + gap100
			pdf.setDrawColor(200, 200, 200)
			pdf.setLineWidth(0.35)
			pdf.line(lineX1, sepY, lineX2, sepY)

			const legendX = textX
			const circleR = (STAGE_MAP_LEGEND_DOT_PX / 2) * designScale
			const lineStep =
				(STAGE_MAP_LEGEND_DOT_PX + STAGE_MAP_LEGEND_INTER_DOT_GAP_PX) * designScale
			const contactGap = STAGE_MAP_LEGEND_CONTACT_SECTION_GAP_PX * designScale
			const legendTop = sepY + gap100
			const legendItems = [
				{ label: 'House & Land', color: '#74895D' },
				{ label: 'Section', color: '#B8CDA4' },
				{ label: 'Townhouse Superlot', color: '#E3D4BD', ring: '#A0947F' },
				{ label: 'Local centre', color: '#8EC2C9' },
				{ label: 'Under offer', color: '#FFAF59' },
				{ label: 'Sold', color: '#EB665E' }
			]
			pdf.setFont('inter', 'normal')
			pdf.setFontSize(STAGE_MAP_LEGEND_LABEL_PX * designScale)
			pdf.setTextColor(...STAGE_MAP_LEGEND_TEXT_COLOR)
			legendItems.forEach((item, idx) => {
				const rowTop = legendTop + idx * lineStep
				const cy = rowTop + circleR
				const [r, g, b] = hexToRgb(item.color)
				pdf.setFillColor(r, g, b)
				pdf.circle(legendX + circleR, cy, circleR, 'F')
				if (item.ring) {
					const [rr, rg, rb] = hexToRgb(item.ring)
					pdf.setDrawColor(rr, rg, rb)
					pdf.setLineWidth(0.5)
					pdf.circle(legendX + circleR, cy, circleR, 'S')
				}
				pdf.setTextColor(...STAGE_MAP_LEGEND_TEXT_COLOR)
				pdf.text(item.label, legendX + circleR * 2 + 6, cy, { baseline: 'middle' })
			})

			const lastRowTop = legendTop + (legendItems.length - 1) * lineStep
			const legendEndY = lastRowTop + 2 * circleR
			const contactRuleY = legendEndY + contactGap
			pdf.setDrawColor(200, 200, 200)
			pdf.setLineWidth(0.35)
			pdf.line(lineX1, contactRuleY, lineX2, contactRuleY)
			const contactImgY = contactRuleY + contactGap
			if (contactMark) {
				const cW = STAGE_MAP_CONTACT_W_PX * designScale
				const cH = cW * (contactMark.naturalHeight / contactMark.naturalWidth)
				pdf.addImage(contactMark.dataUrl, 'PNG', textX, contactImgY, cW, cH)
			}
		}

		const downloadMap = async (stageTitle) => {
		  if (!draw || !mapContent.value) return
		  
		  try {
			if (typeof html2canvas === 'undefined') {
			  console.error('html2canvas library not found')
			  alert('Download functionality requires html2canvas library')
			  return
			}
			
			if (typeof window.jspdf === 'undefined') {
			  console.error('jsPDF library not found')
			  alert('Download functionality requires jsPDF library')
			  return
			}
			
			downloadingStage.value = stageTitle
			console.log('Starting map download...')

			const originalViewBox = draw.viewbox()

			// PDF page/layout geometry is computed up front (rather than after capture) so
			// the stage viewBox below can be zoomed to the SAME aspect ratio as the printed
			// map slot (mapSlotAspect) — this is fixed by the A4 page + design constants,
			// identical on every device/viewport. Framing the stage to that fixed ratio
			// (instead of the live on-screen viewBox ratio, which varies by device) is what
			// makes the stage focus/zoom consistent across devices.
			const { jsPDF } = window.jspdf

			const pdf = new jsPDF('portrait', 'pt', 'a4', true)
			const pageW = pdf.internal.pageSize.getWidth()
			const pageH = pdf.internal.pageSize.getHeight()

			await registerStageMapPdfFonts(pdf)

			let footerH = 48
			let footerImg = null
			try {
				footerImg = await loadStageMapFooterPng()
				footerH = (footerImg.naturalHeight / footerImg.naturalWidth) * pageW
			} catch (e) {
				console.warn('Stage map footer image missing or failed to load; using placeholder height.', e)
			}

			const scale = pageW / STAGE_MAP_DESIGN_W
			const marginPt = STAGE_MAP_MARGIN_PX * scale
			const panelW = STAGE_MAP_SIDEBAR_PX * scale
			const panelX = marginPt
			const panelY = marginPt
			const sidebarMapGap = STAGE_MAP_SIDEBAR_MAP_GAP_PX * scale
			const mapX = marginPt + panelW + sidebarMapGap
			const mapY = marginPt
			const mapW = pageW - marginPt - panelW - sidebarMapGap - marginPt
			const mainH = pageH - marginPt - footerH - marginPt
			const mapH = mainH
			const mapSlotAspect = mapW / mapH

			let zoomedToStage = false
			if (stageTitle) {
			  try { draw.timeline().stop() } catch (e) { /* no active timeline */ }
			  const stagesGroup = draw.findOne('#stages')
			  const stageElement = stagesGroup?.findOne(`#Stage_${stageTitle}`)
			  if (stageElement) {
				const bbox = stageElement.bbox()
				// Margin as a fraction of the stage border's own size (rather than a fixed
				// unit count) — a flat padding is a wildly different proportion depending on
				// how big the stage border is, so small stages ended up with disproportionate
				// dead space around them.
				const marginFraction = 0.08
				const paddedWidth = bbox.width * (1 + marginFraction * 2)
				const paddedHeight = bbox.height * (1 + marginFraction * 2)
				const centerX = bbox.x + bbox.width / 2
				const centerY = bbox.y + bbox.height / 2

				// Fit the padded stage bbox into a viewBox that already matches the printed
				// map slot's aspect ratio (mapSlotAspect), so every device frames the exact
				// same proportion of content around the stage — the later aspect-crop below
				// becomes a no-op instead of a second, device-dependent crop fighting this one.
				let newWidth, newHeight
				if (paddedWidth / paddedHeight > mapSlotAspect) {
					newWidth = paddedWidth
					newHeight = newWidth / mapSlotAspect
				} else {
					newHeight = paddedHeight
					newWidth = newHeight * mapSlotAspect
				}

				draw.viewbox(centerX - newWidth / 2, centerY - newHeight / 2, newWidth, newHeight)
				zoomedToStage = true
			  }
			  await new Promise(resolve => setTimeout(resolve, 300))
			}

			const elementToCapture = mapContent.value

			// Force the capture container to a fixed size sharing mapSlotAspect, on every
			// device (not just mobile) — this is what previously varied the crop by viewport.
			// The container itself is capped at a modest CSS size (STAGE_MAP_MAX_CAPTURE_SIDE_PX);
			// any extra resolution needed to hit the target DPI is pushed into html2canvas's
			// own `scale` multiplier instead, since that only affects the final raster, not
			// the size/complexity of the DOM subtree html2canvas has to clone and rasterize.
			let captureOrigStyle = null
			let captureScale = STAGE_MAP_CAPTURE_SCALE
			if (zoomedToStage) {
				const targetPxScale = STAGE_MAP_TARGET_DPI / 72
				const targetOutW = mapW * targetPxScale
				const targetOutH = mapH * targetPxScale

				const captureCssH = Math.min(
					STAGE_MAP_MAX_CAPTURE_SIDE_PX,
					(targetOutH / STAGE_MAP_CAPTURE_SCALE) * STAGE_MAP_CAPTURE_HEADROOM
				)
				const captureCssW = captureCssH * mapSlotAspect
				captureScale = Math.max(
					STAGE_MAP_CAPTURE_SCALE,
					(targetOutH / captureCssH) * STAGE_MAP_CAPTURE_HEADROOM
				)

				captureOrigStyle = { width: elementToCapture.style.width, height: elementToCapture.style.height }
				elementToCapture.style.width = Math.ceil(captureCssW) + 'px'
				elementToCapture.style.height = Math.ceil(captureCssH) + 'px'
				await new Promise(r => setTimeout(r, 50))
			}

			const canvas = await html2canvas(elementToCapture, {
			  scale: captureScale,
			  useCORS: true,
			  allowTaint: true,
			  backgroundColor: '#ffffff',
			  // Real iOS Safari has long-standing bugs rasterizing complex/transformed SVG
			  // via html2canvas's default foreignObject fast path — often a silent
			  // blank/background-only capture rather than an error. Forcing the manual
			  // (canvg-based) renderer is the standard fix; Chrome's mobile device
			  // emulation never exercises this since it's still the desktop engine, so it
			  // can't catch this class of bug.
			  foreignObjectRendering: false
			})

			if (captureOrigStyle) {
				elementToCapture.style.width = captureOrigStyle.width
				elementToCapture.style.height = captureOrigStyle.height
			}

			const titlesText = stageTitle
				? (availableStages.value?.find((s) => s.title === stageTitle)?.titles || '')
				: ''

			let figureMark = null
			try {
				figureMark = await loadStageMapFigure()
			} catch (e) {
				console.warn('Could not load stage map figure', e)
			}

			let contactMark = null
			try {
				contactMark = await loadStageMapContactPng()
			} catch (e) {
				console.warn('Could not load stagemap-contact.png', e)
			}

			drawStageMapPanel(pdf, {
				x: panelX,
				y: panelY,
				w: panelW,
				h: mainH,
				stageTitle,
				titlesText,
				figureMark,
				contactMark,
				designScale: scale
			})

			const mapAspect = mapW / mapH
			const imgAspect = canvas.width / canvas.height
			let srcX = 0; let srcY = 0; let srcW = canvas.width; let srcH = canvas.height
			if (imgAspect > mapAspect) {
				srcW = canvas.height * mapAspect
				srcX = (canvas.width - srcW) / 2
			} else {
				srcH = canvas.width / mapAspect
				srcY = (canvas.height - srcH) / 2
			}
			const cropped = document.createElement('canvas')
			cropped.width = srcW
			cropped.height = srcH
			cropped.getContext('2d').drawImage(canvas, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH)

			const mapCornerR = STAGE_MAP_MAP_CORNER_RADIUS_PX * scale
			const mapPxScale = STAGE_MAP_TARGET_DPI / 72
			const mapImgDataUrl = clipCroppedMapToRoundRectDataUrl(cropped, mapW, mapH, mapCornerR, mapPxScale)
			pdf.addImage(mapImgDataUrl, 'JPEG', mapX, mapY, mapW, mapH)

			if (footerImg) {
				drawStageMapFooter(pdf, pageW, pageH, footerImg)
			} else {
				pdf.setFillColor(39, 51, 29)
				pdf.rect(0, pageH - footerH, pageW, footerH, 'F')
			}
			
			draw.viewbox(originalViewBox.x, originalViewBox.y, originalViewBox.width, originalViewBox.height)
			
			const title = stageTitle ? `Stage ${stageTitle}` : 'Site Plan'

			if (isPdfPreviewMode()) {
				const blob = pdf.output('blob')
				const blobUrl = URL.createObjectURL(blob)
				const opened = window.open(blobUrl, '_blank')
				if (!opened) {
					alert('Popup blocked — allow popups for this site, or turn off ?pdfPreview=1 for a normal download.')
					URL.revokeObjectURL(blobUrl)
				}
			} else {
				pdf.save('Westhaven ' + title + '.pdf')
			}
			
			console.log('Map downloaded successfully')
			
		  } catch (error) {
			console.error('Error downloading map:', error)
			alert('Error downloading map: ' + error.message)
		  } finally {
			downloadingStage.value = null
		  }
		}

		const closePriceListPreviewTab = (tab) => {
			if (!tab || tab.closed) return
			try {
				tab.close()
			} catch (_) {}
		}

		const beginPriceListDownload = (stageTitle) => {
			downloadingPriceList.value = stageTitle
			const previewTab = isPdfPreviewMode() ? window.open('about:blank', '_blank') : null
			downloadPriceList(stageTitle, previewTab)
		}

		const downloadPriceList = async (stageTitle, previewTab = null) => {
			if (!sectionsData || !availableStages.value.length) {
				console.error('No data available for price list')
				closePriceListPreviewTab(previewTab)
				downloadingPriceList.value = null
				return
			}

			try {
				console.log('Downloading price list for stage:', stageTitle)

				const stage = availableStages.value.find(s => s.title === stageTitle)
				if (!stage) {
					console.error('Stage not found:', stageTitle)
					closePriceListPreviewTab(previewTab)
					downloadingPriceList.value = null
					return
				}

				const stageLots = sectionsData.data.filter(lot => {
					if (!lot.stage || !Array.isArray(lot.stage) || lot.stage.length === 0) {
						return false
					}
					if (lot.stage[0].title !== stageTitle) {
						return false
					}
					if (lot.enabled === false) {
						return false
					}
					if (lot.entryStatus === 'pending') {
						return false
					}
					if (lot.status !== 'available') {
						return false
					}
					return true
				})

				console.log('Found', stageLots.length, 'live lots for stage', stageTitle)

				if (stageLots.length === 0) {
					alert('No live lots found for this stage')
					closePriceListPreviewTab(previewTab)
					downloadingPriceList.value = null
					return
				}

				currentPriceListStage.value = stage
				currentPriceListLots.value = stageLots

				await nextTick()

				if (priceListPdfRef.value) {
					if (isPdfPreviewMode()) {
						await priceListPdfRef.value.previewPdf(previewTab)
					} else {
						await priceListPdfRef.value.generatePdf()
					}
				} else {
					console.error('PriceListPdf component not found')
					closePriceListPreviewTab(previewTab)
				}
			} catch (error) {
				console.error('Error downloading price list:', error)
				closePriceListPreviewTab(previewTab)
				alert('Error downloading price list: ' + error.message)
			} finally {
				downloadingPriceList.value = null
			}
		}

		const refreshTextElements = async () => {
		  await loadSectionsData()
		  updateTextElements()
		}
		
		// Popup methods
		const showLotPopup = (lot, event) => {
		  selectedLot.value = lot
		  showPopup.value = true

		if (scrollOverlayTimer.value) {
			clearTimeout(scrollOverlayTimer.value)
			scrollOverlayTimer.value = null
		}
		showScrollOverlay.value = false
		  
		  // If this is a new entry (no-entry status), load saved stage from cookie and set default values
		  if (lot.status === 'no-entry' && isAdmin.value) {
			// Set default section type to "Section"
			sectionType.value = 'section'
			enabled.value = true
			
			// Load saved stage from cookie, or use first available stage
			const savedStageId = getCookie('site-plan-last-stage')
			if (savedStageId && availableStages.value.some(s => String(s.id) === savedStageId)) {
			  stageId.value = savedStageId
			} else if (availableStages.value.length > 0) {
			  // Use first available stage if no cookie
			  stageId.value = String(availableStages.value[0].id)
			}
			
			// Set default status to "available"
			status.value = 'available'
		  }
		  
		  // Existing entry that is disabled: open in edit mode so admin can re-enable
		  if (lot.id && lot.enabled === false && isAdmin.value) {
			isEditMode.value = true
			startEdit({ stopPropagation: () => {} })
		  }
		}
		
		const closePopup = () => {
		  const wasEditMode = isEditMode.value
		  showPopup.value = false
		  selectedLot.value = null
		  isEditMode.value = false
			// Clear form fields; when closing after edit mode, clear everything including stageId
			if (!wasEditMode) {
				// Keep stageId for next entry (it's in cookie)
				sectionSize.value = ''
				price.value = ''
				status.value = 'available'
				sectionType.value = ''
				enabled.value = true
				postDate.value = ''
			} else {
				// Clear everything when closing edit mode
				stageId.value = ''
				status.value = ''
				sectionSize.value = ''
				price.value = ''
				sectionType.value = ''
				enabled.value = true
				postDate.value = ''
			}
		  // Remove lot parameter from URL when closing popup
		  updateURLWithLot(null)
		}

		// Bulk popup helpers
		const openBulkPopup = async () => {
		  bulkUnlinkedLots.value = await getUnlinkedLots()
		  showBulkPopup.value = true
		}

		const closeBulkPopup = () => {
		  showBulkPopup.value = false
		}

		const openScreenshotModal = () => {
		  showScreenshotModal.value = true
		}

		const closeScreenshotModal = () => {
		  showScreenshotModal.value = false
		}

		const handleBulkCompleted = async () => {
		  // Keep popup open; refresh sections data and map so newly created lots show as connected
		  await loadSectionsData(true)
		  updateTextElements()
		}
		
		// Handle click outside popup and deselect lots
		const handleClickOutside = (event) => {
    		if (isScreenshotMode.value) return

		  // In lot-focused mode, keep the lot selected—never reset on click
		  if (isLotFocusedMode.value) return

		  // Don't close if clicking on popup buttons or form elements
		  if (event.target.closest('.popup-button') || event.target.closest('[data-popup-form]') || event.target.closest('[data-popup-actions]')) {
			return
		  }
		  
		  // Close popup if clicking outside of it
		  if (showPopup.value && !event.target.closest('[data-lot-popup]')) {
			closePopup()
		  }
		  
		  // Only deselect lots if popup is not open and clicking outside of lot elements
		  if (!showPopup.value && !event.target.closest('[id^="_"]')) {
			resetLotSelections()
		  }
		}
		
		// Expose functions globally for testing
		window.resetLotSelections = resetLotSelections
		window.refreshTextElements = refreshTextElements
		
		// Lifecycle
		onMounted(async () => {
			const downloadParams = getDownloadParamsFromUrl()
			if (downloadParams) {
				showDownloadOverlay.value = true
				downloadStageFromUrl.value = downloadParams.stage
				downloadTypeFromUrl.value = downloadParams.download
			}
			
			// Check admin status first
			await checkAdminStatus()
			// Screenshot mode only for logged-in admins
			const urlParams = new URLSearchParams(window.location.search)
			isScreenshotMode.value = urlParams.get('screenshot') === 'true' && isAdmin.value
			
			// Load current map SVG URL
			await loadCurrentMap()

			await loadAllMaps()
			
			// Wait for next tick to ensure DOM is ready
			setTimeout(() => {
				initializeMap()
			}, 100)
		  
		  // Add click outside listener
		  document.addEventListener('click', handleClickOutside)
		  
		  // Add click handler to map container for deselection
		  if (mapWrapper.value) {
			mapWrapper.value.addEventListener('click', (event) => {
			  if (isLotFocusedMode.value) return
			  // Only deselect if popup is not open and clicking on the map background (not on lot elements)
			  if (!showPopup.value && !event.target.closest('[id^="_"]')) {
				resetLotSelections()
			  }
			})
		  }
		})
		
		onUnmounted(() => {
			console.log('[Cleanup] Starting unmount cleanup')
			
			// Remove all lot element handlers first
			cleanupLotHandlers()
			
			if (viewBoxBoundaryZoomRaf != null) {
				cancelAnimationFrame(viewBoxBoundaryZoomRaf)
				viewBoxBoundaryZoomRaf = null
			}
			if (draw) {
				draw.off()  // remove all SVG.js event listeners
				draw.remove()
				draw = null
			}

			// Remove global window listeners
			window.removeEventListener('wheel', onWheelGlobal, { capture: true })
			window.removeEventListener('touchstart', onTouchStartGlobal, { capture: true })
			window.removeEventListener('touchmove', onTouchMoveGlobal, { capture: true })
			window.removeEventListener('touchend', onTouchEndGlobal, { capture: true })
			window.removeEventListener('resize', onLegendViewportResize)
			if (legendResizeRaf != null) {
				cancelAnimationFrame(legendResizeRaf)
				legendResizeRaf = null
			}

			if (mapWrapper.value && preventPageZoomFn) {
				mapWrapper.value.removeEventListener('touchstart', preventPageZoomFn, { capture: true })
				mapWrapper.value.removeEventListener('touchmove', preventPageZoomFn, { capture: true })
			}
			
			// Clear scroll overlay timer
			if (scrollOverlayTimer.value) {
				clearTimeout(scrollOverlayTimer.value)
				scrollOverlayTimer.value = null
			}
			
			// Remove click outside listener
			document.removeEventListener('click', handleClickOutside)
			
			console.log('[Cleanup] Unmount cleanup complete')
		})
		
		return {
		  mapWrapper,
		  mapContent,
		  currentMapSvgUrl,
		  showPopup,
		  selectedLot,
		  closePopup,
		  availableStages,
		  keyStages,
		  isSingleStageMobileMode,
		  isStagesPanelReady,
		  lotFromMount,
		  stageFromMount,
		  embeddedActiveTarget,
		  isLotFocusedMode,
		  zoomToStage,
		  downloadMap,
		  downloadPriceList,
		  beginPriceListDownload,
		  priceListPdfRef,
		  currentPriceListStage,
		  currentPriceListLots,
		  downloadingPriceList,
		  cmsEditUrl,
		  isAdmin,
		  sectionSize,
		  price,
		  stageId,
		  status,
		  sectionType,
		  enabled,
		  isCreatingEntry,
		  isUpdatingEntry,
		  isEditMode,
		  downloadingStage,
		  createEntry,
		  startEdit,
		  cancelEdit,
		  updateEntry,
		  handleStageIdUpdate,
		  getLotFromURL,
		  updateURLWithLot,
		  getStageFromURL,
		  updateURLWithStage,
		  selectLotByNumber,
		  formatPostDateText,
		  // Bulk
		  showBulkPopup,
		  bulkUnlinkedLots,
		  postDate,
		  formatPostDateForInput,
		  openBulkPopup,
		  closeBulkPopup,
		  handleBulkCompleted,
		  showScreenshotModal,
		  sectionsEntriesRef,
		  openScreenshotModal,
		  closeScreenshotModal,
		  mapSwitcherRef,
		  allMaps,
		  activeMapId,
		  isLoadingMaps,
		  handleMapPreview,
		  handleSetActive,
		  handleCancelPreview,
		  isEmbedded,
		  isMobile,
		  modifierKey,
		  showScrollOverlay,
		  dismissScrollOverlay,
		  isScreenshotMode,
		  screenshotOverlayMessage,
		  screenshotStatus,
		  gestureStartedWithMultipleFingers,
		  amenityVisible,
		  amenitiesOpen,
		  keyMapOpen,
		  stagesOpen,
		  lotSearchQuery,
		  isNarrowLegendViewport,
		  toggleAmenity,
		  showDownloadOverlay,
		  downloadStageFromUrl,
		  downloadTypeFromUrl,
		  dismissDownloadOverlay
		}
	  }
}
</script>

<style scoped>
.lot-popup-image--bleed {
	width: calc(100% + 2rem);
	margin-left: -1rem;
	margin-right: -1rem;
}
</style>